﻿
var loadedFromRefresh = null;
function isLoadedFromRefresh() {
    if (loadedFromRefresh !== null) {
        return loadedFromRefresh;
    }
    try {
        if ((window.performance.navigation && window.performance.navigation.type === 1) ||
            window.performance
                .getEntriesByType('navigation')
                .filter((nav) => nav instanceof PerformanceNavigationTiming)
                .map((nav) => nav.type)
                .includes('reload')) {

            loadedFromRefresh = true;
            return true;
        }
    } catch (e) {
        console.error(e);
    }
    loadedFromRefresh = false;
    return false;
}

const ConnectStatsMapState = {
    instance: null,
    countryNames: {},
    getCountryData: function(countryId) {
        return this.countryNames[countryId] || null;
    },
    addCountryData: function(data) {
        for (const countryId in data) {
            if (data.hasOwnProperty(countryId)) {
                this.countryNames[countryId] = data[countryId];
            }
        }
    }
};

class ConnectStatsMap {
    // Constants
    static MAP_WIDTH = 1300;
    static SWIPE_DIRECTIONAL_THRESHOLD = 6;
    static RESIZE_DEBOUNCE_MS = 150;
    static DEFAULT_FADE_DURATION_MS = 300;
    static MIN_ZOOM = 0.1;
    static MAX_ZOOM = 11.0;
    static ZOOM_STEP = 0.2;
    static STZOOM_STEP = 1;
    static STZOOM_DYNAMIC_BELOW_ONE_STEPS = 3;
    static PINCH_ZOOM_THRESHOLD = 10;
    static PINCH_RESIZE_COOLDOWN_MS = 220;
    static PINCH_ELASTIC_RESISTANCE = 0.28;
    static PINCH_ELASTIC_MAX_OVERSHOOT = 0.85;
    static PINCH_ELASTIC_MIN_OVERSHOOT = 0.22;
    static STZOOM_BELOW_ONE_LEVELS = [0.5, 0.333333, 0.25, 0.2, 0.166667, 0.125, 0.1];

    /**
     * Interactive map panning and scrolling controller
      * @param {*} container - Container selector or element
     * @param {Object} options - Configuration options
     */
    constructor(container, options = {}) {
        this.$container = /** @type {any} */ ($(/** @type {any} */ (container)));

        // Validate required elements exist
        if (!this.$container.length) {
            console.error('ConnectStatsMap: Container not found');
            return;
        }

        this.$map = this.$container.find('.stmap');
        this.$stmapA = this.$map.find('.stmapA');
        this.$mapViewport = this.$map.parent();
        this.$scroll = this.$container.find('.stscroll .stbar .in');
        this.$button = this.$container.find('.stscroll .stbar .in button');
        this.$debugCrosshair = this.$container.find('.debug-crosshair');
        this.$zoomDisplay = this.$container.find('.currentzoom');
        this.$zoomContainer = this.$container.find('.stzoom');

        // Validate required child elements
        if (!this.$map.length || !this.$stmapA.length || !this.$button.length) {
            console.error('ConnectStatsMap: Required child elements not found');
            return;
        }

        this.defaultCenterOffset = options.defaultCenterOffset ?? 10;
        this.minButtonWidth = options.minButtonWidth ?? 64;
        this.touchActivationThreshold = options.touchActivationThreshold ?? 12;
        this.touchVerticalOffset = options.touchVerticalOffset ?? 24;
        this.touchVerticalOffsetLocked = options.touchVerticalOffsetLocked ?? 120;
        this.allowSimultaneousSwipeScroll = options.allowSimultaneousSwipeScroll ?? true;
        this.forceJsScrollOnMapGesture = options.forceJsScrollOnMapGesture ?? false;
        this.safariGestureGuard = options.safariGestureGuard ?? true;
        this.safariAxisSplitMode = options.safariAxisSplitMode ?? true;
        this.inertiaEnabled = options.inertiaEnabled ?? false;
        this.disableInertiaOnSafariTouch = options.disableInertiaOnSafariTouch ?? true;
        this.inertiaFriction = this.clamp(options.inertiaFriction ?? 0.92, 0.75, 0.99);
        this.inertiaMinVelocity = options.inertiaMinVelocity ?? 0.02;
        this.inertiaMaxDuration = options.inertiaMaxDuration ?? 900;
        this.fadeDuration = options.fadeDuration ?? ConnectStatsMap.DEFAULT_FADE_DURATION_MS;
        this.enableZoom = options.enableZoom ?? false;
        this.enablePinchMapZoom = options.enablePinchMapZoom ?? this.enableZoom;
        this.useDynamicStzoomBelowOneLevels = options.useDynamicStzoomBelowOneLevels ?? true;
        this.stzoomBelowOneStepCount = Math.max(1, Math.round(
            options.stzoomBelowOneStepCount ?? ConnectStatsMap.STZOOM_DYNAMIC_BELOW_ONE_STEPS
        ));
        this.desktopMinZoomScreenThreshold = Math.max(0, options.desktopMinZoomScreenThreshold ?? 800);
        this.desktopMinZoomMapWidth = Math.max(0, options.desktopMinZoomMapWidth ?? 800);
        this.zoomViewportBottomOffset = options.zoomViewportBottomOffset ?? 30;
        this.enableVerticalCentering = options.enableVerticalCentering ?? false;
        this.debug = options.debug ?? false;
        this.defaultZoom = this.resolveDefaultZoom(options.defaultZoom);
        this.initialZoomAnimationDuration = Math.max(0, options.initialZoomAnimationDuration ?? 2220);
        this.initialScrollAnimationDuration = Math.max(0, options.initialScrollAnimationDuration ?? 2220);
        const resolvedDefaultActivePoint = this.resolveDefaultActivePointFromMap();
        const activePointValue = options.activePoint ?? resolvedDefaultActivePoint.x;
        const activePointYValue = options.activePointY ?? resolvedDefaultActivePoint.y;
        this.activePointPx = this.resolveActivePointPx(activePointValue);
        this.activePointPy = this.resolveActivePointPy(activePointYValue);
        this.isFollowingDefaultCenter = true;
        this.isFollowingDefaultCenterSuspended = false;
        this.touchState = null;
        this.isTouchActionLocked = false;
        this.resizeTimeout = null;
        this.zoomScrollRaf = null;
        this.pointerPinchFallbackHandlers = null;
        this.pointerTouchPoints = new Map();
        this.currentZoom = 1.0;
        this.initialViewAnimationRaf = null;
        this.initialViewAnimationTargetZoom = this.currentZoom;
        this.pinchState = null;
        this.lastPinchEndedAt = 0;
        this.enablePan = false;
        this.panState = null;
        this.inertiaState = null;
        this.inertiaFrame = null;
        this.isNativeTouchScrollBlocked = false;
        this.originalScrollRestoration = null;
        this.hasManualScrollRestoration = false;
        this.isLikelySafariTouch = this.detectLikelySafariTouchEnvironment();
        this.useSafariGestureGuard = !!(this.safariGestureGuard && this.isLikelySafariTouch);
        this.handleNativeTouchMoveBlocker = this.handleNativeTouchMoveBlocker.bind(this);
        this.handleZoomHotkeyKeydown = this.handleZoomHotkeyKeydown.bind(this);

        this.setManualScrollRestoration();

        // Initialize CSS custom property for zoom scale
        this.$container.css('--zoom-scale', this.currentZoom);

        this.currentMapLeft = this.getCurrentMapLeft();
        this.recalculateMetrics();
        this.applyDefaultZoom();

        if (isLoadedFromRefresh()) {
            window.scrollTo(0, 0);
        }

        this.applyInitialPosition();
        this.updateDebugCrosshair();
        this.updateZoomControls();

        this.handleResize = this.handleResize.bind(this);
        this.handleWindowScroll = this.handleWindowScroll.bind(this);

        $(window).on('resize', this.handleResize);
        $(window).on('scroll', this.handleWindowScroll);

        this.initEvents();
        this.setTouchActionLocked(false);
        this.updateZoomContainerVerticalOffset();

        // Restore map position from sessionStorage (back navigation)
        this._tryRestoreMapState();
    }

    resolveActivePointPx(activePoint) {
        if (typeof activePoint === 'number' && Number.isFinite(activePoint)) {
            return this.clamp(activePoint, 0, ConnectStatsMap.MAP_WIDTH);
        }

        if (typeof activePoint === 'string') {
            const value = activePoint.trim();
            if (value.endsWith('%')) {
                const percentValue = parseFloat(value.slice(0, -1));
                if (Number.isFinite(percentValue)) {
                    return this.clamp((ConnectStatsMap.MAP_WIDTH * percentValue) / 100, 0, ConnectStatsMap.MAP_WIDTH);
                }
            }

            const pxValue = parseFloat(value);
            if (Number.isFinite(pxValue)) {
                return this.clamp(pxValue, 0, ConnectStatsMap.MAP_WIDTH);
            }
        }

        return 650;
    }

    resolveDefaultZoom(defaultZoom) {
        const parsedZoom = parseFloat(defaultZoom);
        if (!Number.isFinite(parsedZoom) || parsedZoom <= 0) {
            return 1.0;
        }

        return this.clamp(parsedZoom, ConnectStatsMap.MIN_ZOOM, ConnectStatsMap.MAX_ZOOM);
    }

    resolveActivePointPy(activePointY) {
        const MAP_HEIGHT = 646;
        if (typeof activePointY === 'number' && Number.isFinite(activePointY)) {
            return this.clamp(activePointY, 0, MAP_HEIGHT);
        }

        if (typeof activePointY === 'string') {
            const value = activePointY.trim();
            if (value.endsWith('%')) {
                const percentValue = parseFloat(value.slice(0, -1));
                if (Number.isFinite(percentValue)) {
                    return this.clamp((MAP_HEIGHT * percentValue) / 100, 0, MAP_HEIGHT);
                }
            }

            const pxValue = parseFloat(value);
            if (Number.isFinite(pxValue)) {
                return this.clamp(pxValue, 0, MAP_HEIGHT);
            }
        }

        return 323; // default center (646/2)
    }

    resolveDefaultActivePointFromMap() {
        const fallback = {
            x: '50%',
            y: '50%'
        };

        if (typeof MapStarsUtils === 'undefined' || !MapStarsUtils.getDefaultActiveStar) {
            return fallback;
        }

        const $activeStar = MapStarsUtils.getDefaultActiveStar(this.$container);
        if (!$activeStar.length) {
            return fallback;
        }

        const left = parseFloat($activeStar.css('left'));
        const top = parseFloat($activeStar.css('top'));

        return {
            x: !isNaN(left) ? left : fallback.x,
            y: !isNaN(top) ? top : fallback.y
        };
    }

    /**
     * Clamp value between min and max
     * @private
     */
    clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    }

    /**
     * Effective minimum zoom based on viewport, with optional desktop floor map width
     * @private
     */
    getEffectiveMinZoom() {
        //const viewportWidth = Number.isFinite(this.viewportWidth) ? this.viewportWidth : this.getMapViewportWidth();
        const viewportWidth = this.getMapViewportWidthWithPaddings();
        const screenWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const useDesktopMinMapWidth = screenWidth > this.desktopMinZoomScreenThreshold;
        const minMapWidth = useDesktopMinMapWidth
            ? Math.min(viewportWidth, this.desktopMinZoomMapWidth)
            : viewportWidth;
        const fitToWidthZoom = minMapWidth > 0
            ? (minMapWidth / ConnectStatsMap.MAP_WIDTH)
            : ConnectStatsMap.MIN_ZOOM;

        return this.clamp(fitToWidthZoom, ConnectStatsMap.MIN_ZOOM, 1.0);
    }

    /**
     * Whether map should currently follow default center
     * @private
     */
    shouldFollowDefaultCenter() {
        // temporarily disable following default center logic, as it causes more issues than it solves in current state
        return false;
        // return this.isFollowingDefaultCenter && !this.isFollowingDefaultCenterSuspended;
    }

    /**
     * Detect Safari-like touch environments where simultaneous native+JS scroll is unstable
     * @private
     */
    detectLikelySafariTouchEnvironment() {
        const nav = window.navigator;
        if (!nav) {
            return false;
        }

        const ua = nav.userAgent || '';
        const platform = nav.platform || '';
        const maxTouchPoints = Number(nav.maxTouchPoints || 0);
        const isIOSLike = /iP(ad|hone|od)/i.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);
        const isSafariCore = /AppleWebKit/i.test(ua) && (/Safari/i.test(ua) || isIOSLike);
        const isKnownAltBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/i.test(ua);

        return isIOSLike && isSafariCore && !isKnownAltBrowser;
    }

    /**
     * Resolve effective simultaneous swipe+scroll mode
     * @private
     */
    isSimultaneousSwipeScrollEnabled() {
        return !!this.allowSimultaneousSwipeScroll && !this.useSafariGestureGuard;
    }

    /**
     * Resolve Safari axis-split mode: native vertical + custom horizontal only
     * @private
     */
    isSafariAxisSplitModeEnabled() {
        return !!this.safariAxisSplitMode && !!this.isLikelySafariTouch;
    }

    /**
     * Resolve whether vertical movement should be JS-only during active map gestures
     * @private
     */
    isJsVerticalScrollOnlyEnabled() {
        return this.isSimultaneousSwipeScrollEnabled() && !!this.forceJsScrollOnMapGesture;
    }

    /**
     * Native touchmove blocker for JS-only scroll mode
     * @private
     */
    handleNativeTouchMoveBlocker(event) {
        if (!this.isJsVerticalScrollOnlyEnabled() || !this.touchState) {
            return;
        }

        if (event.touches && event.touches.length !== 1) {
            return;
        }

        event.preventDefault();
    }

    /**
     * Toggle native touch scroll blocking state
     * @private
     */
    setNativeTouchScrollBlocked(blocked) {
        if (this.isNativeTouchScrollBlocked === blocked) {
            return;
        }

        this.isNativeTouchScrollBlocked = blocked;
        if (blocked) {
            document.addEventListener('touchmove', this.handleNativeTouchMoveBlocker, { passive: false });
            return;
        }

        document.removeEventListener('touchmove', this.handleNativeTouchMoveBlocker);
    }

    /**
     * Get current map left position
     * @private
     */
    getCurrentMapLeft() {
        const mapLeft = parseFloat(this.$map.css('left'));
        return Number.isFinite(mapLeft) ? mapLeft : 0;
    }

    /**
     * Recalculate metrics after resize
     * @private
     */
    recalculateMetrics() {
        this.mapWidth = ConnectStatsMap.MAP_WIDTH * this.currentZoom;
        this.viewportWidth = this.getMapViewportWidth();
        //const viewportWidthWithPaddings = this.getMapViewportWidthWithPaddings();
        this.scrollWidth = this.$scroll.width();

        this.maxMapShift = Math.max(this.mapWidth - this.viewportWidth, 0);

        const canScroll = this.maxMapShift > 0;
        const idealButtonWidth = canScroll
            ? this.scrollWidth * (this.viewportWidth / this.mapWidth)
            : this.scrollWidth;

        this.buttonWidth = canScroll
            ? this.clamp(idealButtonWidth, this.minButtonWidth, this.scrollWidth)
            : this.scrollWidth;

        this.$button.css('width', this.buttonWidth + 'px');
        this.maxScrollLeft = Math.max(this.scrollWidth - this.buttonWidth, 0);

        const isMobileViewport = this.viewportWidth <= 980;
        const isCoarsePointer = typeof window.matchMedia === 'function'
            && window.matchMedia('(pointer: coarse)').matches;
        const effectiveMinZoom = this.getEffectiveMinZoom();
        const isAtEffectiveMinZoom = this.currentZoom <= (effectiveMinZoom + 0.0001);
        const hideScrollAtMobileMinZoom = isAtEffectiveMinZoom && (isMobileViewport || isCoarsePointer);

        const alwaysShowScroll = false; // TODO fix;
        this.$container.toggleClass('stscroll-hidden', alwaysShowScroll ? false : (!canScroll || hideScrollAtMobileMinZoom));
    }

    getMapViewportWidth() {
        const width = this.$mapViewport.width();
        //const paddings = parseFloat(this.$mapViewport.css('padding-left')) + parseFloat(this.$mapViewport.css('padding-right'));
        return width;// + paddings;
    }

    getMapViewportWidthWithPaddings() {
        const width = this.$mapViewport.outerWidth();
        //const paddings = parseFloat(this.$mapViewport.css('padding-left')) + parseFloat(this.$mapViewport.css('padding-right'));
        return this.$mapViewport.outerWidth();
    }

    /**
     * Restore map zoom and scroll position from history.state (back/forward navigation)
     * @private
     */
    _tryRestoreMapState() {
        try {
            const state = history.state && history.state.stmap;
            if (!state) {
                return;
            }

            this.cancelInitialViewAnimation();

            const zoom = parseFloat(state.zoom);
            if (Number.isFinite(zoom) && zoom > 0 && this.enableZoom) {
                const effectiveMinZoom = this.getEffectiveMinZoom();
                this.currentZoom = this.clamp(zoom, effectiveMinZoom, ConnectStatsMap.MAX_ZOOM);
                this.$container.css('--zoom-scale', this.currentZoom);
                this.$map.css({
                    width: (ConnectStatsMap.MAP_WIDTH * this.currentZoom) + 'px',
                    height: (646 * this.currentZoom) + 'px'
                });
                this.recalculateMetrics();
                this.updateZoomControls();
            }

            const mapLeft = parseFloat(state.mapLeft);
            if (Number.isFinite(mapLeft)) {
                this.setMapLeft(mapLeft);
            }

            const scrollY = parseFloat(state.scrollY);
            if (!isLoadedFromRefresh() && Number.isFinite(scrollY) && scrollY > 0) {
                requestAnimationFrame(() => {
                    window.scrollTo(0, scrollY);
                });
            }
        } catch (e) {
            // Ignore restore errors
        }
    }

    /**
     * Set map left position with bounds checking
     * @private
     */
    setMapLeft(mapLeft) {
        if (this.maxMapShift <= 0) {
            const centeredLeft = (this.viewportWidth - this.mapWidth) / 2;
            this.currentMapLeft = Number.isFinite(centeredLeft) ? centeredLeft : 0;
            this.$map.css('left', this.currentMapLeft + 'px');
            this.syncButtonToMap();
            this.updateDebugCrosshair();
            return;
        }

        const minMapLeft = -this.maxMapShift;
        this.currentMapLeft = this.clamp(mapLeft, minMapLeft, 0);
        this.$map.css('left', this.currentMapLeft + 'px');
        this.syncButtonToMap();
        this.updateDebugCrosshair();
    }

    /**
     * Apply configured default zoom during initialization
     * @private
     */
    applyDefaultZoom() {
        if (!this.enableZoom) {
            this.currentZoom = 1.0;
            this.$container.css('--zoom-scale', this.currentZoom);
            return;
        }

        const effectiveMinZoom = this.getEffectiveMinZoom();
        this.initialViewAnimationTargetZoom = this.clamp(this.defaultZoom, effectiveMinZoom, ConnectStatsMap.MAX_ZOOM);
        const shouldAnimateInitialZoom = this.enableZoom
            && this.initialZoomAnimationDuration > 0
            && Math.abs(this.initialViewAnimationTargetZoom - 1.0) > 0.0001;
        this.currentZoom = shouldAnimateInitialZoom ? 1.0 : this.initialViewAnimationTargetZoom;
        this.$container.css('--zoom-scale', this.currentZoom);

        this.$map.css({
            width: (ConnectStatsMap.MAP_WIDTH * this.currentZoom) + 'px',
            height: (646 * this.currentZoom) + 'px'
        });

        this.recalculateMetrics();
    }

    /**
     * Set zoom level
     * @param {number} zoom - Zoom level (0.5 - 2.0)
     * @param {number} centerX - X coordinate to zoom towards (optional)
     */
    setZoom(zoom, centerX = null, meta = null) {
        if (!this.enableZoom) {
            return;
        }

        const oldZoom = this.currentZoom;
        const effectiveMinZoom = this.getEffectiveMinZoom();
        const allowElastic = !!(meta && meta.elastic === true);
        const minElasticZoom = Math.max(0.05, effectiveMinZoom - ConnectStatsMap.PINCH_ELASTIC_MIN_OVERSHOOT);
        const maxElasticZoom = ConnectStatsMap.MAX_ZOOM + ConnectStatsMap.PINCH_ELASTIC_MAX_OVERSHOOT;
        const newZoom = allowElastic
            ? this.clamp(zoom, minElasticZoom, maxElasticZoom)
            : this.clamp(zoom, effectiveMinZoom, ConnectStatsMap.MAX_ZOOM);

        if (oldZoom === newZoom) {
            return;
        }

        const explicitCenterX = centerX !== null;

        // Calculate center point to maintain
        if (centerX === null) {
            centerX = this.viewportWidth / 2;
        }

        // Current map X coordinate at center point (needed if not following default center)
        const mapXAtCenter = centerX - this.currentMapLeft;
        const oldMapViewportRect = this.$mapViewport[0].getBoundingClientRect();
        const viewportCenterY = window.innerHeight / 2;
        const oldMapHeight = 646 * oldZoom;

        // Save vertical position before zoom (if vertical centering enabled and following default center)
        let oldActivePointScreenY = null;
        if (this.enableVerticalCentering && this.shouldFollowDefaultCenter() && !explicitCenterX) {
            const mapViewportRect = this.$mapViewport[0].getBoundingClientRect();
            const oldScaledActivePointPy = this.activePointPy * oldZoom;
            oldActivePointScreenY = mapViewportRect.top + oldScaledActivePointPy;
        }

        this.currentZoom = newZoom;

        // Update CSS custom property for use in inverse scaling
        this.$container.css('--zoom-scale', newZoom);

        // Update .stmap size to accommodate zoomed content
        const originalWidth = ConnectStatsMap.MAP_WIDTH;
        const originalHeight = 646;
        const newWidth = originalWidth * newZoom;
        const newHeight = originalHeight * newZoom;
        this.$map.css({
            width: newWidth + 'px',
            height: newHeight + 'px'
        });

        // Recalculate metrics with new zoom
        this.recalculateMetrics();

        // Position logic similar to resize handler
        // Temporarily disable following default center
        if (this.shouldFollowDefaultCenter() && !explicitCenterX) {
            // Follow the active point (isme/ismax star)
            this.applyDefaultActivePoint();

            // Maintain vertical position (keep activePoint at same screen position)
            if (this.enableVerticalCentering && oldActivePointScreenY !== null) {
                const newMapViewportRect = this.$mapViewport[0].getBoundingClientRect();
                const newScaledActivePointPy = this.activePointPy * this.currentZoom;
                const newActivePointScreenY = newMapViewportRect.top + newScaledActivePointPy;
                const scrollAdjustment = newActivePointScreenY - oldActivePointScreenY;
                window.scrollBy(0, scrollAdjustment);
            }
        } else {
            // Adjust position to keep the same point at center
            const newMapXAtCenter = mapXAtCenter * (newZoom / oldZoom);
            const newMapLeft = centerX - newMapXAtCenter;
            this.setMapLeft(newMapLeft);

            if (this.enableVerticalCentering) {
                const newMapViewportRect = this.$mapViewport[0].getBoundingClientRect();
                const newMapHeight = 646 * newZoom;
                let scrollAdjustment = 0;

                const centerLineIntersectsMap = oldMapViewportRect.top < viewportCenterY
                    && oldMapViewportRect.bottom > viewportCenterY;

                if (centerLineIntersectsMap) {
                    const mapYAtViewportCenter = viewportCenterY - oldMapViewportRect.top;
                    const scaledMapYAtViewportCenter = mapYAtViewportCenter * (newZoom / oldZoom);
                    const newCenterPointScreenY = newMapViewportRect.top + scaledMapYAtViewportCenter;
                    scrollAdjustment = newCenterPointScreenY - viewportCenterY;
                } else if (oldMapViewportRect.bottom <= viewportCenterY) {
                    scrollAdjustment = newMapHeight - oldMapHeight;
                }

                if (Math.abs(scrollAdjustment) > 0.1) {
                    window.scrollBy(0, scrollAdjustment);
                }
            }
        }

        // Update zoom controls if they exist
        this.updateZoomControls();

        // Trigger event for info box repositioning
        this.$container.trigger('mapZoomChanged', {
            zoom: newZoom,
            source: meta && typeof meta.source === 'string' ? meta.source : 'unknown'
        });
        this.updateZoomContainerVerticalOffset();
    }

    /**
     * Handle window scroll for .stzoom vertical follow
     * @private
     */
    handleWindowScroll() {
        if (this.zoomScrollRaf !== null) {
            return;
        }

        this.zoomScrollRaf = requestAnimationFrame(() => {
            this.zoomScrollRaf = null;
            this.updateZoomContainerVerticalOffset();
        });
    }

    /**
     * Keep .stzoom near viewport bottom, clamped to map container bounds
     * @private
     */
    updateZoomContainerVerticalOffset() {
        if (!this.$zoomContainer || !this.$zoomContainer.length || !this.$container.length) {
            return;
        }

        const containerEl = this.$container[0];
        const zoomEl = this.$zoomContainer[0];
        if (!containerEl || !zoomEl) {
            return;
        }

        const containerRect = containerEl.getBoundingClientRect();
        const zoomHeight = this.$zoomContainer.outerHeight() || zoomEl.offsetHeight || 0;
        const desiredViewportBottom = window.innerHeight - this.zoomViewportBottomOffset;
        const canFloat = containerRect.bottom > desiredViewportBottom
            && containerRect.top < (desiredViewportBottom - zoomHeight);

        if (canFloat) {
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
            const fixedWidth = Math.min(viewportWidth, 1560);
            const fixedLeftOffset = -Math.max(Math.min((Math.max(viewportWidth, 1300) - 1300) / 2, 130), 20);
            const fixedLeft = containerRect.left + fixedLeftOffset;

            this.$zoomContainer
                .addClass('stzoom-fixed')
                .css({
                    left: fixedLeft + 'px',
                    width: fixedWidth + 'px',
                    bottom: this.zoomViewportBottomOffset + 'px'
                });
            return;
        }

        this.$zoomContainer
            .removeClass('stzoom-fixed')
            .css({
                left: '',
                width: '',
                bottom: '0px'
            });
    }

    /**
     * Zoom in
     */
    zoomIn(centerX = null) {
        this.setZoom(this.currentZoom + ConnectStatsMap.ZOOM_STEP, centerX);
    }

    /**
     * Zoom out
     */
    zoomOut(centerX = null) {
        this.setZoom(this.currentZoom - ConnectStatsMap.ZOOM_STEP, centerX);
    }

    /**
     * Ignore zoom hotkeys while the user is typing into editable UI.
     * @private
     */
    shouldIgnoreZoomHotkeyEvent(event) {
        if (!event) {
            return true;
        }

        if (event.ctrlKey || event.altKey || event.metaKey) {
            return true;
        }

        const target = event.target;
        if (!target || !(target instanceof Element)) {
            return false;
        }

        if (target.closest('input, textarea, select, [contenteditable="true"], [contenteditable=""], .select2-search__field')) {
            return true;
        }

        return this.isAdminEditInteractionTarget(target);
    }

    /**
     * Handle keyboard zoom hotkeys.
     * @private
     */
    handleZoomHotkeyKeydown(event) {
        if (!this.enableZoom || this.shouldIgnoreZoomHotkeyEvent(event)) {
            return;
        }

        const key = typeof event.key === 'string' ? event.key : '';
        const code = typeof event.code === 'string' ? event.code : '';
        const isZoomInKey = key === '+' || key === '=' || code === 'NumpadAdd';
        const isZoomOutKey = key === '-' || code === 'NumpadSubtract';

        if (!isZoomInKey && !isZoomOutKey) {
            return;
        }

        event.preventDefault();

        if (isZoomInKey) {
            this.triggerZoomIn();
            return;
        }

        this.triggerZoomOut();
    }

    /**
     * Resolve whether a trigger should use stzoom behavior.
     * @private
     */
    isStzoomTrigger($trigger) {
        return !!($trigger && $trigger.length && $trigger.closest('.stzoom').length > 0);
    }

    /**
     * Resolve the preferred zoom control for keyboard hotkeys.
     * @private
     */
    getPreferredZoomTrigger(selector) {
        const $stzoomTrigger = this.$container.find(`.stzoom ${selector}`).first();
        if ($stzoomTrigger.length) {
            return $stzoomTrigger;
        }

        return this.$container.find(selector).first();
    }

    /**
     * Execute zoom-in action using the same logic as UI buttons.
     * @private
     */
    triggerZoomIn($trigger = null) {
        const $resolvedTrigger = $trigger && $trigger.length
            ? $trigger
            : this.getPreferredZoomTrigger('.zoom-in');

        if ($resolvedTrigger.length && ($resolvedTrigger.prop('disabled') || $resolvedTrigger.hasClass('inactive'))) {
            return;
        }

        if (this.isStzoomTrigger($resolvedTrigger)) {
            this.setZoom(this.getNextStzoomZoomIn(this.currentZoom));
            return;
        }

        this.setZoom(this.currentZoom + ConnectStatsMap.ZOOM_STEP);
    }

    /**
     * Execute zoom-out action using the same logic as UI buttons.
     * @private
     */
    triggerZoomOut($trigger = null) {
        const $resolvedTrigger = $trigger && $trigger.length
            ? $trigger
            : this.getPreferredZoomTrigger('.zoom-out');

        if ($resolvedTrigger.length && ($resolvedTrigger.prop('disabled') || $resolvedTrigger.hasClass('inactive'))) {
            return;
        }

        if (this.isStzoomTrigger($resolvedTrigger)) {
            this.setZoom(this.getNextStzoomZoomOut(this.currentZoom));
            return;
        }

        this.setZoom(this.currentZoom - ConnectStatsMap.ZOOM_STEP);
    }

    /**
     * Get stzoom levels below 1.0.
     * Dynamic mode splits the effective range into equal steps; static mode uses the legacy list.
     * @private
     */
    getStzoomBelowOneLevels(effectiveMinZoom = this.getEffectiveMinZoom()) {
        const eps = 0.0001;
        if (effectiveMinZoom >= (1 - eps)) {
            return [];
        }

        if (!this.useDynamicStzoomBelowOneLevels) {
            return ConnectStatsMap.STZOOM_BELOW_ONE_LEVELS.filter((level) => level >= (effectiveMinZoom - eps));
        }

        const stepCount = Math.max(1, this.stzoomBelowOneStepCount);
        const totalRange = 1.0 - effectiveMinZoom;
        const levels = [];

        for (let index = 1; index <= stepCount; index++) {
            const rawLevel = index === stepCount
                ? effectiveMinZoom
                : (1.0 - ((totalRange * index) / stepCount));
            const normalizedLevel = this.clamp(Number(rawLevel.toFixed(6)), effectiveMinZoom, 1.0);
            levels.push(normalizedLevel);
        }

        return levels.filter((level, index) => {
            if (level >= (1 - eps) || level < (effectiveMinZoom - eps)) {
                return false;
            }

            return index === 0 || Math.abs(level - levels[index - 1]) > eps;
        });
    }

    /**
     * Get next stzoom zoom-in level (supports non-linear steps below 1.0)
     * @private
     */
    getNextStzoomZoomIn(currentZoom) {
        const zoom = Number.isFinite(currentZoom) ? currentZoom : this.currentZoom;
        const eps = 0.0001;
        const levels = this.getStzoomBelowOneLevels();

        if (zoom < 1 - eps) {
            for (let index = levels.length - 1; index >= 0; index--) {
                const level = levels[index];
                if (zoom <= (level + eps)) {
                    if (index > 0) {
                        return levels[index - 1];
                    }

                    return 1.0;
                }
            }

            return 1.0;
        }

        const roundedZoom = Math.round(zoom);
        const nextWholeZoom = roundedZoom + ConnectStatsMap.STZOOM_STEP;
        return this.clamp(nextWholeZoom, 1.0, ConnectStatsMap.MAX_ZOOM);
    }

    /**
     * Get next stzoom zoom-out level (supports non-linear steps below 1.0)
     * @private
     */
    getNextStzoomZoomOut(currentZoom) {
        const zoom = Number.isFinite(currentZoom) ? currentZoom : this.currentZoom;
        const eps = 0.0001;
        const effectiveMinZoom = this.getEffectiveMinZoom();

        if (zoom > 1 + eps) {
            const wholePart = Math.floor(zoom + eps);
            const isWholeZoom = Math.abs(zoom - wholePart) <= eps;
            const targetWholeZoom = isWholeZoom
                ? (wholePart - ConnectStatsMap.STZOOM_STEP)
                : (Math.round(zoom) - ConnectStatsMap.STZOOM_STEP);

            return Math.max(1.0, Math.max(effectiveMinZoom, targetWholeZoom));
        }

        const levels = this.getStzoomBelowOneLevels(effectiveMinZoom);
        for (let index = 0; index < levels.length; index++) {
            const level = levels[index];
            if (zoom > (level + eps)) {
                return level;
            }

            if (Math.abs(zoom - level) <= eps && index < levels.length - 1) {
                return levels[index + 1];
            }
        }

        return effectiveMinZoom;
    }

    /**
     * Reset zoom to 1.0
     */
    resetZoom() {
        const wasDefaultZoom = Math.abs(this.currentZoom - 1.0) <= 0.0001;
        this.setZoom(1.0, null, { source: 'reset' });

        if (wasDefaultZoom) {
            this.$container.trigger('mapZoomChanged', {
                zoom: this.currentZoom,
                source: 'reset'
            });
        }
    }

    /**
     * Update zoom control button states
     * @private
     */
    updateZoomControls() {
        const $zoomIn = this.$container.find('.zoom-in');
        const $zoomOut = this.$container.find('.zoom-out');
        const effectiveMinZoom = this.getEffectiveMinZoom();
        const eps = 0.0001;
        const isZoomInInactive = this.currentZoom >= (ConnectStatsMap.MAX_ZOOM - eps);
        const isZoomOutInactive = this.currentZoom <= (effectiveMinZoom + eps);

        if ($zoomIn.length) {
            $zoomIn
                .prop('disabled', isZoomInInactive)
                .toggleClass('inactive', isZoomInInactive)
                .attr('aria-disabled', isZoomInInactive ? 'true' : 'false');
        }
        if ($zoomOut.length) {
            $zoomOut
                .prop('disabled', isZoomOutInactive)
                .toggleClass('inactive', isZoomOutInactive)
                .attr('aria-disabled', isZoomOutInactive ? 'true' : 'false');
        }

        // Update zoom percentage display
        this.updateZoomDisplay();
    }

    /**
     * Update zoom percentage display
     * @private
     */
    updateZoomDisplay() {
        if (this.$zoomDisplay.length) {
            const eps = 0.0001;
            const isDefaultZoom = Math.abs(this.currentZoom - 1.0) <= eps;

            if (isDefaultZoom) {
                this.$zoomDisplay
                    .text('0')
                    .addClass('is-default-zoom')
                    .attr('aria-hidden', 'true');
                return;
            }

            this.$zoomDisplay.removeClass('is-default-zoom').attr('aria-hidden', 'false');

            let displayValue = 0;
            if (this.currentZoom >= 1.0) {
                displayValue = Math.round((this.currentZoom - 1.0) * 100);
            } else {
                displayValue = -Math.round(((1 / this.currentZoom) - 1) * 100);
            }

            const displayText = displayValue > 0
                ? ('+' + String(displayValue))
                : String(displayValue);
            this.$zoomDisplay.text(displayText);
        }
    }

    /**
     * Synchronize scroll button position with map
     * @private
     */
    syncButtonToMap() {
        if (this.maxMapShift <= 0 || this.maxScrollLeft <= 0) {
            this.$button.css('left', '0px');
            return;
        }

        const scrollRatio = (-this.currentMapLeft) / this.maxMapShift;
        const buttonLeft = this.clamp(scrollRatio * this.maxScrollLeft, 0, this.maxScrollLeft);
        this.$button.css('left', buttonLeft + 'px');
    }

    /**
     * Apply default active point positioning
     * @private
     */
    applyDefaultActivePoint(options = {}) {
        const skipVerticalCentering = !!options.skipVerticalCentering;

        if (this.maxMapShift <= 0) {
            this.setMapLeft(0);
            return;
        }

        // Horizontal positioning
        const targetViewportX = (this.viewportWidth / 2) - this.defaultCenterOffset;
        // Apply zoom to active point position
        const scaledActivePointPx = this.activePointPx * this.currentZoom;
        const targetMapLeft = targetViewportX - scaledActivePointPx;
        this.setMapLeft(targetMapLeft);
        if (!skipVerticalCentering && MapStarsUtils.needCenterVertically(this.$container)) {
            this.centerActivePointVertically();
        }

        this.updateDebugCrosshair();
    }

    /**
     * Center active point vertically by scrolling the window
     * @private
     */
    centerActivePointVertically() {
        // pagaidām nelietojam, bet ja vajag centrēt vertikāli, tad šī funkcija scrollos tā, lai activePointPy būtu viewporta vidū
        // Calculate scaled Y position
        const scaledActivePointPy = this.activePointPy * this.currentZoom;

        // Get map viewport position in global coordinates
        const mapViewportRect = this.$mapViewport[0].getBoundingClientRect();

        // Calculate where the activePointY is in global viewport coordinates
        const activePointGlobalY = mapViewportRect.top + scaledActivePointPy;

        // Calculate target scroll to center this point in viewport
        const viewportCenterY = window.innerHeight / 2;
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
        const targetScrollY = currentScrollY + (activePointGlobalY - viewportCenterY);

        // Scroll to target position
        window.scrollTo({
            top: targetScrollY,
            behavior: 'smooth'
        });
    }

    shouldAnimateInitialView() {
        const needsZoomAnimation = this.enableZoom
            && this.initialZoomAnimationDuration > 0
            && Math.abs(this.initialViewAnimationTargetZoom - 1.0) > 0.0001;
        const needsVerticalCentering = typeof MapStarsUtils !== 'undefined'
            && MapStarsUtils.needCenterVertically
            && MapStarsUtils.needCenterVertically(this.$container);
        const needsScrollAnimation = needsVerticalCentering && this.initialScrollAnimationDuration > 0;

        return needsZoomAnimation || needsScrollAnimation;
    }

    cancelInitialViewAnimation() {
        if (this.initialViewAnimationRaf !== null) {
            cancelAnimationFrame(this.initialViewAnimationRaf);
            this.initialViewAnimationRaf = null;
        }
    }

    setManualScrollRestoration() {
        if (!('scrollRestoration' in history) || this.hasManualScrollRestoration) {
            return;
        }

        this.originalScrollRestoration = history.scrollRestoration;
        history.scrollRestoration = 'manual';
        this.hasManualScrollRestoration = true;
    }

    restoreScrollRestoration() {
        if (!this.hasManualScrollRestoration || !('scrollRestoration' in history)) {
            return;
        }

        history.scrollRestoration = this.originalScrollRestoration || 'auto';
        this.hasManualScrollRestoration = false;
        this.originalScrollRestoration = null;
    }

    getInitialScrollTargetY(targetZoom = this.initialViewAnimationTargetZoom) {
        if (typeof MapStarsUtils === 'undefined'
            || !MapStarsUtils.needCenterVertically
            || !MapStarsUtils.needCenterVertically(this.$container)
            || !this.$mapViewport.length
            || !this.$mapViewport[0]) {
            return null;
        }

        const viewportCenterY = window.innerHeight / 2;
        const pageScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const mapViewportTop = this.$mapViewport[0].getBoundingClientRect().top + pageScrollY;

        return mapViewportTop + (this.activePointPy * targetZoom) - viewportCenterY;
    }

    animateInitialView() {
        this.cancelInitialViewAnimation();

        const startZoom = this.currentZoom;
        const targetZoom = this.initialViewAnimationTargetZoom;
        const startScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        const targetScrollY = this.getInitialScrollTargetY(targetZoom);
        const needsZoomAnimation = this.enableZoom
            && this.initialZoomAnimationDuration > 0
            && Math.abs(targetZoom - startZoom) > 0.0001;
        const needsScrollAnimation = Number.isFinite(targetScrollY)
            && this.initialScrollAnimationDuration > 0
            && Math.abs(targetScrollY - startScrollY) > 0.5;

        if (!needsZoomAnimation && !needsScrollAnimation) {
            if (Number.isFinite(targetScrollY)) {
                window.scrollTo(0, targetScrollY);
            }

            this.updateZoomControls();
            return;
        }

        const startTime = performance.now();
        const tick = (now) => {
            const zoomProgress = needsZoomAnimation
                ? this.clamp((now - startTime) / this.initialZoomAnimationDuration, 0, 1)
                : 1;
            const scrollProgress = needsScrollAnimation
                ? this.clamp((now - startTime) / this.initialScrollAnimationDuration, 0, 1)
                : 1;

            if (needsZoomAnimation) {
                const easedZoomProgress = this.easeOutCubic(zoomProgress);
                const nextZoom = startZoom + ((targetZoom - startZoom) * easedZoomProgress);
                this.currentZoom = nextZoom;
                this.$container.css('--zoom-scale', nextZoom);
                this.$map.css({
                    width: (ConnectStatsMap.MAP_WIDTH * nextZoom) + 'px',
                    height: (646 * nextZoom) + 'px'
                });
                this.recalculateMetrics();
                this.applyDefaultActivePoint({ skipVerticalCentering: true });
            }

            if (needsScrollAnimation) {
                const easedScrollProgress = this.easeOutCubic(scrollProgress);
                const nextScrollY = startScrollY + ((targetScrollY - startScrollY) * easedScrollProgress);
                window.scrollTo(0, nextScrollY);
            }

            this.updateZoomControls();

            if (zoomProgress < 1 || scrollProgress < 1) {
                this.initialViewAnimationRaf = requestAnimationFrame(tick);
                return;
            }

            this.initialViewAnimationRaf = null;
            if (needsZoomAnimation) {
                this.currentZoom = targetZoom;
                this.$container.css('--zoom-scale', targetZoom);
                this.$map.css({
                    width: (ConnectStatsMap.MAP_WIDTH * targetZoom) + 'px',
                    height: (646 * targetZoom) + 'px'
                });
                this.recalculateMetrics();
                this.applyDefaultActivePoint({ skipVerticalCentering: true });
            }

            if (Number.isFinite(targetScrollY)) {
                window.scrollTo(0, targetScrollY);
            }

            this.updateZoomControls();
            this.$container.trigger('mapZoomChanged', {
                zoom: this.currentZoom,
                source: 'init'
            });
        };

        this.initialViewAnimationRaf = requestAnimationFrame(tick);
    }

    easeOutCubic(t) {
        const clampedT = this.clamp(t, 0, 1);
        return 1 - Math.pow(1 - clampedT, 3);
    }

    /**
     * Center map on specific X coordinate
     * @private
     */
    setCenterMapX(centerMapX) {
        if (this.maxMapShift <= 0) {
            this.setMapLeft(0);
            return;
        }

        const targetMapLeft = (this.viewportWidth / 2) - centerMapX;
        this.setMapLeft(targetMapLeft);
    }

    /**
     * Apply initial map position
     * @private
     */
    applyInitialPosition() {
        if (this.shouldAnimateInitialView()) {
            this.applyDefaultActivePoint({ skipVerticalCentering: true });

            if (this.fadeDuration !== ConnectStatsMap.DEFAULT_FADE_DURATION_MS) {
                const durationSeconds = this.fadeDuration / 1000;
                this.$container.find('.stmap, .stscroll').css('transition', `opacity ${durationSeconds}s ease`);
            }

            this.$container.addClass('ready');

            requestAnimationFrame(() => {
                this.animateInitialView();
            });
            return;
        }

        this.applyDefaultActivePoint();
        // Set fade-in duration and show map after position is set
        if (this.fadeDuration !== ConnectStatsMap.DEFAULT_FADE_DURATION_MS) {
            const durationSeconds = this.fadeDuration / 1000;
            this.$container.find('.stmap, .stscroll').css('transition', `opacity ${durationSeconds}s ease`);
        }
        this.$container.addClass('ready');
    }

    /**
     * Set touch action locked state
     * @private
     */
    setTouchActionLocked(locked) {
        this.isTouchActionLocked = locked;
        const allowSimultaneousSwipeScroll = this.isSimultaneousSwipeScrollEnabled();
        const safariAxisSplitMode = this.isSafariAxisSplitModeEnabled();
        const touchActionValue = (() => {
            if (locked) {
                return 'none';
            }

            if (this.enablePinchMapZoom) {
                return 'pan-y';
            }

            return (allowSimultaneousSwipeScroll || safariAxisSplitMode) ? 'pan-y pinch-zoom' : '';
        })();

        this.$mapViewport.css('touchAction', touchActionValue);
        this.$stmapA.css('touchAction', touchActionValue);
        this.$button.css('touchAction', touchActionValue);
    }

    /**
     * In pointer-enabled browsers (notably iOS Safari), force custom JS pinch and block native browser zoom.
     * @private
     */
    bindPointerModePinchFallback() {
        if (!this.enablePinchMapZoom || !this.$mapViewport.length || this.pointerPinchFallbackHandlers) {
            return;
        }

        const viewportEl = this.$mapViewport[0];
        if (!viewportEl) {
            return;
        }

        const onTouchStart = (event) => {
            if (!this.enablePinchMapZoom || !event.touches || event.touches.length !== 2) {
                return;
            }

            const touch = event.touches[0];
            const target = touch ? document.elementFromPoint(touch.clientX, touch.clientY) : event.target;
            if (this.isAdminEditBoxActive() || this.isAdminEditInteractionTarget(target)) {
                return;
            }

            this.endSwipe();
            this.startPinch(event.touches[0], event.touches[1]);
            event.preventDefault();
        };

        const onTouchMove = (event) => {
            if (!this.enablePinchMapZoom || !event.touches || event.touches.length !== 2) {
                return;
            }

            this.movePinch(event.touches[0], event.touches[1]);
            event.preventDefault();
        };

        const onTouchEndOrCancel = (event) => {
            const touches = event && event.touches ? event.touches.length : 0;
            if (touches < 2) {
                this.endPinch();
            }
        };

        const onGesture = (event) => {
            if (!this.enablePinchMapZoom) {
                return;
            }

            event.preventDefault();
        };

        viewportEl.addEventListener('touchstart', onTouchStart, { passive: false });
        viewportEl.addEventListener('touchmove', onTouchMove, { passive: false });
        viewportEl.addEventListener('touchend', onTouchEndOrCancel, { passive: false });
        viewportEl.addEventListener('touchcancel', onTouchEndOrCancel, { passive: false });
        viewportEl.addEventListener('gesturestart', onGesture, { passive: false });
        viewportEl.addEventListener('gesturechange', onGesture, { passive: false });
        viewportEl.addEventListener('gestureend', onGesture, { passive: false });

        this.pointerPinchFallbackHandlers = {
            viewportEl,
            onTouchStart,
            onTouchMove,
            onTouchEndOrCancel,
            onGesture
        };
    }

    /**
     * @private
     */
    unbindPointerModePinchFallback() {
        if (!this.pointerPinchFallbackHandlers) {
            return;
        }

        const {
            viewportEl,
            onTouchStart,
            onTouchMove,
            onTouchEndOrCancel,
            onGesture
        } = this.pointerPinchFallbackHandlers;

        if (viewportEl) {
            viewportEl.removeEventListener('touchstart', onTouchStart);
            viewportEl.removeEventListener('touchmove', onTouchMove);
            viewportEl.removeEventListener('touchend', onTouchEndOrCancel);
            viewportEl.removeEventListener('touchcancel', onTouchEndOrCancel);
            viewportEl.removeEventListener('gesturestart', onGesture);
            viewportEl.removeEventListener('gesturechange', onGesture);
            viewportEl.removeEventListener('gestureend', onGesture);
        }

        this.pointerPinchFallbackHandlers = null;
    }

    /**
     * Check if map is vertically outside viewport
     * @private
     */
    isMapVerticallyOutsideViewport() {
        const mapRect = this.$mapViewport[0].getBoundingClientRect();
        return mapRect.top < 0 || mapRect.bottom > $(window).height();
    }

    /**
     * Check if admin edit box is currently active
     * @private
     */
    isAdminEditBoxActive() {
        return this.$container.find('.stmap-edit.active').length > 0;
    }

    /**
     * Check whether event target is inside admin edit UI controls
     * @private
     */
    isAdminEditInteractionTarget(target) {
        if (!target) {
            return false;
        }

        const $target = $(target);
        return $target.closest('.stmap-edit, .select2-container, .select2-dropdown, .select2-search__field').length > 0;
    }

    /**
     * Start swipe gesture
     * @private
     */
    startSwipe(clientX, clientY, source = 'map') {
        this.stopInertia();

        const normalizedSource = source === 'button' ? 'button' : 'map';
        if (normalizedSource === 'button') {
            this.setTouchActionLocked(true);
        }

        if (this.isJsVerticalScrollOnlyEnabled()) {
            this.setNativeTouchScrollBlocked(true);
        }

        if (this.maxMapShift <= 0) {
            this.touchState = null;
            return;
        }

        this.touchState = {
            startX: clientX,
            startY: clientY,
            lastX: clientX,
            lastY: clientY,
            accumulatedX: 0,
            startMapLeft: this.currentMapLeft,
            startButtonLeft: parseFloat(this.$button.css('left')) || 0,
            source: normalizedSource,
            disableInertia: normalizedSource === 'button',
            mode: null,
            lastMoveTime: performance.now(),
            velocityX: 0,
            velocityY: 0,
            didHandle: false
        };
    }

    /**
     * Start pinch gesture for zooming
     * @private
     */
    startPinch(touch1, touch2) {
        if (!this.enableZoom) {
            return;
        }

        this.stopInertia();
        this.setNativeTouchScrollBlocked(false);
        this.isFollowingDefaultCenterSuspended = true;

        const distance = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
        );

        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;

        // Convert to map viewport coordinates
        const viewportRect = this.$mapViewport[0].getBoundingClientRect();
        const mapViewportX = centerX - viewportRect.left;

        this.pinchState = {
            startDistance: distance,
            startZoom: this.currentZoom,
            centerX: mapViewportX
        };
    }

    /**
     * Handle pinch movement
     * @private
     */
    movePinch(touch1, touch2) {
        if (!this.pinchState || !this.enableZoom) {
            return;
        }

        const distance = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
        );

        const distanceChange = distance - this.pinchState.startDistance;

        if (Math.abs(distanceChange) < ConnectStatsMap.PINCH_ZOOM_THRESHOLD) {
            return;
        }

        const zoomFactor = distance / this.pinchState.startDistance;
        const rawZoom = this.pinchState.startZoom * zoomFactor;
        const effectiveMinZoom = this.getEffectiveMinZoom();
        let newZoom = rawZoom;

        if (rawZoom > ConnectStatsMap.MAX_ZOOM) {
            const overshoot = rawZoom - ConnectStatsMap.MAX_ZOOM;
            newZoom = ConnectStatsMap.MAX_ZOOM + (overshoot * ConnectStatsMap.PINCH_ELASTIC_RESISTANCE);
        } else if (rawZoom < effectiveMinZoom) {
            const undershoot = effectiveMinZoom - rawZoom;
            newZoom = effectiveMinZoom - (undershoot * ConnectStatsMap.PINCH_ELASTIC_RESISTANCE);
        }

        this.setZoom(newZoom, this.pinchState.centerX, { source: 'pinch', elastic: true });
    }

    /**
     * @private
     */
    updatePointerTouchPoint(pointerId, clientX, clientY) {
        if (!Number.isFinite(pointerId)) {
            return;
        }

        this.pointerTouchPoints.set(pointerId, {
            clientX,
            clientY
        });
    }

    /**
     * @private
     */
    removePointerTouchPoint(pointerId) {
        if (!Number.isFinite(pointerId)) {
            return;
        }

        this.pointerTouchPoints.delete(pointerId);
    }

    /**
     * @private
     */
    startOrMovePointerPinch() {
        if (!this.enablePinchMapZoom || this.pointerTouchPoints.size < 2) {
            return false;
        }

        const points = Array.from(this.pointerTouchPoints.values());
        const p1 = points[0];
        const p2 = points[1];
        if (!p1 || !p2) {
            return false;
        }

        if (!this.pinchState) {
            this.endSwipe();
            this.startPinch(p1, p2);
        } else {
            this.movePinch(p1, p2);
        }

        return true;
    }

    /**
     * End pinch gesture
     * @private
     */
    endPinch() {
        const pinchCenterX = this.pinchState ? this.pinchState.centerX : null;
        this.pinchState = null;
        this.lastPinchEndedAt = performance.now();
        this.isFollowingDefaultCenterSuspended = false;

        const effectiveMinZoom = this.getEffectiveMinZoom();
        const clampedZoom = this.clamp(this.currentZoom, effectiveMinZoom, ConnectStatsMap.MAX_ZOOM);
        if (Math.abs(clampedZoom - this.currentZoom) > 0.0001) {
            this.setZoom(clampedZoom, pinchCenterX, { source: 'pinch-release-clamp' });
        }

        if (!this.touchState) {
            this.setNativeTouchScrollBlocked(false);
        }
    }

    /**
     * Handle swipe movement
     * @private
     */
    moveSwipe(clientX, clientY) {
        if (!this.touchState || this.maxMapShift <= 0) {
            return { handled: false, blockVertical: false };
        }

        if (this.touchState.source === 'button') {
            this.setTouchActionLocked(true);

            if (this.isFollowingDefaultCenter) {
                this.isFollowingDefaultCenter = false;
                this.updateDebugCrosshair();
            }

            const deltaX = clientX - this.touchState.startX;
            const previousMapLeft = this.currentMapLeft;

            let targetMapLeft = this.currentMapLeft;
            if (this.maxScrollLeft > 0 && this.maxMapShift > 0) {
                const startButtonLeft = Number.isFinite(this.touchState.startButtonLeft)
                    ? this.touchState.startButtonLeft
                    : (parseFloat(this.$button.css('left')) || 0);
                const newButtonLeft = this.clamp(startButtonLeft + deltaX, 0, this.maxScrollLeft);
                targetMapLeft = -(newButtonLeft / this.maxScrollLeft) * this.maxMapShift;
            }

            this.setMapLeft(targetMapLeft);
            const appliedMapDeltaX = this.currentMapLeft - previousMapLeft;

            const now = performance.now();
            const dt = Math.max(now - this.touchState.lastMoveTime, 1);
            const instantVelocityX = appliedMapDeltaX / dt;
            this.touchState.velocityX = (this.touchState.velocityX * 0.7) + (instantVelocityX * 0.3);
            this.touchState.velocityY = 0;
            this.touchState.lastMoveTime = now;
            this.touchState.lastX = clientX;
            this.touchState.lastY = clientY;
            this.touchState.didHandle = true;

            return { handled: true, blockVertical: true };
        }

        const allowSimultaneousSwipeScroll = this.isSimultaneousSwipeScrollEnabled();
        const jsVerticalOnly = this.isJsVerticalScrollOnlyEnabled();
        const safariAxisSplitMode = this.isSafariAxisSplitModeEnabled();
        const deltaX = clientX - this.touchState.startX;
        const deltaY = clientY - this.touchState.startY;
        let horizontalStepX = 0;

        if (allowSimultaneousSwipeScroll) {
            const prevX = Number.isFinite(this.touchState.lastX) ? this.touchState.lastX : this.touchState.startX;
            const stepX = clientX - prevX;
            horizontalStepX = stepX;
            this.touchState.lastX = clientX;
            this.touchState.accumulatedX = (this.touchState.accumulatedX || 0) + stepX;

            if (this.touchState.mode !== 'horizontal' && Math.abs(this.touchState.accumulatedX) < this.touchActivationThreshold) {
                const stepYBeforeHorizontalLock = clientY - this.touchState.lastY;
                if (jsVerticalOnly && stepYBeforeHorizontalLock !== 0) {
                    const appliedScrollDeltaY = -stepYBeforeHorizontalLock;
                    window.scrollBy(0, appliedScrollDeltaY);

                    const now = performance.now();
                    const dt = Math.max(now - this.touchState.lastMoveTime, 1);
                    const instantVelocityY = appliedScrollDeltaY / dt;
                    this.touchState.velocityY = (this.touchState.velocityY * 0.7) + (instantVelocityY * 0.3);
                    this.touchState.lastMoveTime = now;
                    this.touchState.didHandle = true;
                }

                this.touchState.lastY = clientY;
                return {
                    handled: jsVerticalOnly && stepYBeforeHorizontalLock !== 0,
                    blockVertical: jsVerticalOnly
                };
            }

            this.touchState.mode = 'horizontal';
        } else {
            if (!this.touchState.mode) {
                if (
                    Math.abs(deltaX) < this.touchActivationThreshold &&
                    Math.abs(deltaY) < this.touchActivationThreshold
                ) {
                    return { handled: false, blockVertical: false };
                }

                this.touchState.mode = Math.abs(deltaX) > (Math.abs(deltaY) + ConnectStatsMap.SWIPE_DIRECTIONAL_THRESHOLD) ? 'horizontal' : 'vertical';
            }

            if (this.touchState.mode !== 'horizontal') {
                return { handled: false, blockVertical: false };
            }
        }

        const allowVerticalWhileHorizontal = safariAxisSplitMode
            ? false
            : (allowSimultaneousSwipeScroll || this.isMapVerticallyOutsideViewport());
        this.setTouchActionLocked(!allowSimultaneousSwipeScroll || jsVerticalOnly);

        if (!allowSimultaneousSwipeScroll) {
            const mapRect = this.$mapViewport[0].getBoundingClientRect();
            const activeVerticalOffset = this.touchState.mode === 'horizontal'
                ? this.touchVerticalOffsetLocked
                : this.touchVerticalOffset;
            const isWithinVerticalBounds =
                clientY >= (mapRect.top - activeVerticalOffset) &&
                clientY <= (mapRect.bottom + activeVerticalOffset);

            if (!isWithinVerticalBounds) {
                this.touchState = null;
                this.setTouchActionLocked(false);
                return { handled: false, blockVertical: false };
            }
        }

        if (this.isFollowingDefaultCenter) {
            this.isFollowingDefaultCenter = false;
            this.updateDebugCrosshair();
        }

        const targetMapLeft = allowSimultaneousSwipeScroll
            ? (this.currentMapLeft + horizontalStepX)
            : (this.touchState.startMapLeft + deltaX);
        const previousMapLeft = this.currentMapLeft;
        this.setMapLeft(targetMapLeft);
        const appliedMapDeltaX = this.currentMapLeft - previousMapLeft;

        const stepY = clientY - this.touchState.lastY;
        let appliedScrollDeltaY = 0;
        if (!safariAxisSplitMode) {
            appliedScrollDeltaY = -stepY;
            if (stepY !== 0) {
                if (allowSimultaneousSwipeScroll) {
                    window.scrollBy(0, appliedScrollDeltaY);
                } else if (allowVerticalWhileHorizontal) {
                    window.scrollBy(0, appliedScrollDeltaY);
                }
            }
        }

        const now = performance.now();
        const dt = Math.max(now - this.touchState.lastMoveTime, 1);
        const instantVelocityX = appliedMapDeltaX / dt;
        const instantVelocityY = appliedScrollDeltaY / dt;
        this.touchState.velocityX = (this.touchState.velocityX * 0.7) + (instantVelocityX * 0.3);
        this.touchState.velocityY = (this.touchState.velocityY * 0.7) + (instantVelocityY * 0.3);
        this.touchState.lastMoveTime = now;
        this.touchState.didHandle = true;
        this.touchState.lastY = clientY;

        return { handled: true, blockVertical: !allowSimultaneousSwipeScroll || jsVerticalOnly };
    }

    /**
     * End swipe gesture
     * @private
     */
    endSwipe() {
        const touchState = this.touchState;
        this.touchState = null;
        this.setTouchActionLocked(false);
        this.setNativeTouchScrollBlocked(false);

        if (!touchState || !touchState.didHandle) {
            return;
        }

        if (touchState.disableInertia) {
            return;
        }

        this.startInertia(touchState.velocityX || 0, touchState.velocityY || 0);
    }

    /**
     * Enable or disable pan mode
     * @param {boolean} enabled - Whether pan mode should be enabled
     */
    setPanEnabled(enabled) {
        this.enablePan = !!enabled;
        if (!enabled && this.panState) {
            this.endPan();
        }

        // Update cursor based on pan mode state
        if (this.enablePan) {
            const addModeActive = this.$container.attr('data-add-active') === '1';
            this.$stmapA.css('cursor', addModeActive ? 'crosshair' : (this.maxMapShift > 0 ? 'grab' : ''));
        } else {
            this.$stmapA.css('cursor', '');
        }
    }

    /**
     * Start pan gesture (mouse drag)
     * @private
     */
    startPan(clientX, clientY) {
        if (!this.enablePan || this.maxMapShift <= 0) {
            return;
        }

        this.stopInertia();

        this.panState = {
            startX: clientX,
            startY: clientY,
            lastX: clientX,
            lastY: clientY,
            startMapLeft: this.currentMapLeft,
            startScrollY: window.pageYOffset || document.documentElement.scrollTop,
            lastMoveTime: performance.now(),
            velocityX: 0,
            velocityY: 0,
            didHandle: false
        };

        if (this.isFollowingDefaultCenter) {
            this.isFollowingDefaultCenter = false;
            this.updateDebugCrosshair();
        }

        // Change cursor
        this.$stmapA.css('cursor', 'grabbing');
    }

    /**
     * Handle pan movement
     * @private
     */
    movePan(clientX, clientY) {
        if (!this.panState || !this.enablePan) {
            return;
        }

        const stepX = clientX - this.panState.lastX;
        const stepY = clientY - this.panState.lastY;
        const deltaX = clientX - this.panState.startX;
        const deltaY = clientY - this.panState.startY;

        // Update horizontal position
        const previousMapLeft = this.currentMapLeft;
        const targetMapLeft = this.panState.startMapLeft + deltaX;
        this.setMapLeft(targetMapLeft);
        const appliedMapDeltaX = this.currentMapLeft - previousMapLeft;

        // Update vertical position by scrolling window
        const targetScrollY = this.panState.startScrollY - deltaY;
        const previousScrollY = window.pageYOffset || document.documentElement.scrollTop;
        window.scrollTo(0, targetScrollY);
        const nextScrollY = window.pageYOffset || document.documentElement.scrollTop;
        const appliedScrollDeltaY = nextScrollY - previousScrollY;

        const now = performance.now();
        const dt = Math.max(now - this.panState.lastMoveTime, 1);
        const instantVelocityX = appliedMapDeltaX / dt;
        const instantVelocityY = appliedScrollDeltaY / dt;
        this.panState.velocityX = (this.panState.velocityX * 0.7) + (instantVelocityX * 0.3);
        this.panState.velocityY = (this.panState.velocityY * 0.7) + (instantVelocityY * 0.3);
        this.panState.lastMoveTime = now;
        this.panState.lastX = clientX;
        this.panState.lastY = clientY;
        this.panState.didHandle = this.panState.didHandle || stepX !== 0 || stepY !== 0;
    }

    /**
     * End pan gesture
     * @private
     */
    endPan() {
        const panState = this.panState;
        this.panState = null;

        if (panState && panState.didHandle) {
            this.startInertia(panState.velocityX || 0, panState.velocityY || 0);
        }

        if (this.enablePan) {
            const addModeActive = this.$container.attr('data-add-active') === '1';
            this.$stmapA.css('cursor', addModeActive ? 'crosshair' : (this.maxMapShift > 0 ? 'grab' : ''));
        } else {
            this.$stmapA.css('cursor', '');
        }
    }

    /**
     * Stop active inertia animation
     * @private
     */
    stopInertia() {
        if (this.inertiaFrame !== null) {
            cancelAnimationFrame(this.inertiaFrame);
            this.inertiaFrame = null;
        }

        this.inertiaState = null;
    }

    /**
     * Start inertia animation based on gesture velocity
     * @private
     */
    startInertia(velocityX, velocityY) {
        if (!this.inertiaEnabled) {
            return;
        }

        if (this.disableInertiaOnSafariTouch && this.isLikelySafariTouch) {
            return;
        }

        const minVelocity = Math.max(this.inertiaMinVelocity, 0);
        if (Math.abs(velocityX) < minVelocity && Math.abs(velocityY) < minVelocity) {
            return;
        }

        this.stopInertia();

        const startedAt = performance.now();
        this.inertiaState = {
            vx: velocityX,
            vy: velocityY,
            lastTime: startedAt,
            startedAt: startedAt
        };

        const tick = (timestamp) => {
            if (!this.inertiaState) {
                return;
            }

            const dt = Math.max(timestamp - this.inertiaState.lastTime, 1);
            this.inertiaState.lastTime = timestamp;

            const horizontalBefore = this.currentMapLeft;
            this.setMapLeft(this.currentMapLeft + (this.inertiaState.vx * dt));
            const horizontalApplied = this.currentMapLeft - horizontalBefore;

            const scrollBefore = window.pageYOffset || document.documentElement.scrollTop;
            if (this.inertiaState.vy !== 0) {
                window.scrollBy(0, this.inertiaState.vy * dt);
            }
            const scrollAfter = window.pageYOffset || document.documentElement.scrollTop;
            const verticalApplied = scrollAfter - scrollBefore;

            const decay = Math.pow(this.inertiaFriction, dt / 16.67);
            this.inertiaState.vx *= decay;
            this.inertiaState.vy *= decay;

            const elapsed = timestamp - this.inertiaState.startedAt;
            const isTooSlow =
                Math.abs(this.inertiaState.vx) < minVelocity &&
                Math.abs(this.inertiaState.vy) < minVelocity;
            const noMovementPossible =
                Math.abs(horizontalApplied) < 0.01 &&
                Math.abs(verticalApplied) < 0.01;

            if (elapsed >= this.inertiaMaxDuration || isTooSlow || noMovementPossible) {
                this.stopInertia();
                return;
            }

            this.inertiaFrame = requestAnimationFrame(tick);
        };

        this.inertiaFrame = requestAnimationFrame(tick);
    }

    /**
     * Handle window resize
     * @private
     */
    handleResize() {
        // Debounce resize events to improve performance
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
            const previousViewportWidth = this.viewportWidth;
            const currentMapLeft = this.currentMapLeft;
            const now = performance.now();
            const isPinchResize = !!this.pinchState
                || this.isFollowingDefaultCenterSuspended
                || ((now - this.lastPinchEndedAt) < ConnectStatsMap.PINCH_RESIZE_COOLDOWN_MS);

            this.recalculateMetrics();

            const effectiveMinZoom = this.getEffectiveMinZoom();
            if (this.currentZoom < (effectiveMinZoom - 0.0001)) {
                this.setZoom(effectiveMinZoom, null, { source: 'resize-min-clamp' });
                this.resizeTimeout = null;
                return;
            }

            if (this.shouldFollowDefaultCenter()) {
                this.applyDefaultActivePoint();
                this.updateZoomContainerVerticalOffset();
                this.resizeTimeout = null;
                return;
            }

            if (isPinchResize) {
                // Keep user-selected map position after pinch; only clamp to new bounds
                this.setMapLeft(currentMapLeft);
            } else {
                // Real viewport resize: keep previous visual center
                const centerMapX = -currentMapLeft + (previousViewportWidth / 2);
                this.setCenterMapX(centerMapX);
            }

            this.updateDebugCrosshair();
            this.updateZoomControls();
            this.updateZoomContainerVerticalOffset();
            this.resizeTimeout = null;
        }, ConnectStatsMap.RESIZE_DEBOUNCE_MS);
    }

    /**
     * Initialize all event listeners
     * @private
     */
    initEvents() {
        // ===== PAN MODE: Mouse drag navigation (admin mode only) =====
        // Allows dragging the map with mouse (both horizontal and vertical)
        // Does not activate on stars - stars will have their own drag handler with delay
        this.$stmapA.on('mousedown', (e) => {
            if (!this.enablePan) {
                return;
            }

            if (this.isAdminEditBoxActive() || this.isAdminEditInteractionTarget(e.target)) {
                return;
            }

            // Skip if clicking on a star (stars will have separate drag logic)
            if ($(e.target).closest('.stm-star').length > 0) {
                return;
            }

            e.preventDefault();
            this.startPan(e.clientX, e.clientY);
        });

        $(document).on('mousemove.connectStatsMapPan', (e) => {
            if (this.panState) {
                e.preventDefault();
                this.movePan(e.clientX, e.clientY);
            }
        });

        $(document).on('mouseup.connectStatsMapPan', () => {
            if (this.panState) {
                this.endPan();
            }
        });

        // ===== SCROLL BUTTON: Manual dragging of the horizontal scroll indicator =====
        // Allows precise control of horizontal position via the scroll bar button
        this.$button.on('mousedown', (e) => {
            if (this.isAdminEditBoxActive() || this.isAdminEditInteractionTarget(e.target)) {
                return;
            }

            e.preventDefault();
            this.isFollowingDefaultCenter = false;
            const startX = e.pageX;
            const startButtonLeft = parseFloat(this.$button.css('left'));

            const onMouseMove = (e) => {
                if (this.maxScrollLeft <= 0 || this.maxMapShift <= 0) {
                    return;
                }

                const deltaX = e.pageX - startX;
                const newButtonLeft = Math.min(Math.max(startButtonLeft + deltaX, 0), this.maxScrollLeft);
                this.$button.css('left', newButtonLeft + 'px');

                const mapLeft = -(newButtonLeft / this.maxScrollLeft) * this.maxMapShift;
                this.currentMapLeft = mapLeft;
                this.$map.css('left', this.currentMapLeft + 'px');
            };

            const onMouseUp = () => {
                $(document).off('mousemove', onMouseMove);
                $(document).off('mouseup', onMouseUp);
            };

            $(document).on('mousemove', onMouseMove);
            $(document).on('mouseup', onMouseUp);

            this.updateDebugCrosshair();
        });

        // ===== ZOOM CONTROLS: Zoom in/out/reset buttons =====
        // Desktop zoom controls (buttons in top-right corner)
        if (this.enableZoom) {
            this.$container.find('.zoom-in').on('click', (e) => {
                e.preventDefault();
                this.triggerZoomIn($(e.currentTarget));
            });

            this.$container.find('.zoom-out').on('click', (e) => {
                e.preventDefault();
                this.triggerZoomOut($(e.currentTarget));
            });

            this.$container.find('.zoom-reset').on('click', (e) => {
                e.preventDefault();
                this.resetZoom();
            });

            $(document).on('keydown.connectStatsMapZoomHotkeys', this.handleZoomHotkeyKeydown);
        }

        // ===== TOUCH/POINTER NAVIGATION: Swipe and pinch gestures =====
        // Modern browsers with PointerEvent support
        if ('PointerEvent' in window) {
            const isPrimaryPointer = (event) => {
                const originalEvent = /** @type {any} */ ((event && event.originalEvent) ? event.originalEvent : event);
                return !originalEvent || originalEvent.isPrimary !== false;
            };

            const getPointerMeta = (event) => {
                const originalEvent = /** @type {any} */ ((event && event.originalEvent) ? event.originalEvent : event);
                const pointerType = String((originalEvent && originalEvent.pointerType) || '').toLowerCase();
                const pointerId = originalEvent && Number.isFinite(originalEvent.pointerId)
                    ? originalEvent.pointerId
                    : null;
                return {
                    pointerType,
                    pointerId
                };
            };

            // Map viewport: Horizontal swipe navigation
            // Does not activate on stars - stars will have their own drag handler with delay
            this.$mapViewport.on('pointerdown', (e) => {
                const pointerMeta = getPointerMeta(e);
                const useTouchFallbackPinch = !!this.pointerPinchFallbackHandlers;
                if (!useTouchFallbackPinch && pointerMeta.pointerType === 'touch' && pointerMeta.pointerId !== null) {
                    this.updatePointerTouchPoint(pointerMeta.pointerId, e.clientX, e.clientY);

                    if (this.startOrMovePointerPinch()) {
                        e.preventDefault();
                        return;
                    }
                }

                if (!isPrimaryPointer(e)) {
                    return;
                }

                if (this.isAdminEditBoxActive() || this.isAdminEditInteractionTarget(e.target)) {
                    return;
                }

                // Skip if pointer is on a star (stars will have separate drag logic)
                if ($(e.target).closest('.stm-star').length > 0) {
                    return;
                }

                this.startSwipe(e.clientX, e.clientY);
            });

            this.$mapViewport.on('pointermove', (e) => {
                const pointerMeta = getPointerMeta(e);
                const useTouchFallbackPinch = !!this.pointerPinchFallbackHandlers;
                if (!useTouchFallbackPinch && pointerMeta.pointerType === 'touch' && pointerMeta.pointerId !== null && this.pointerTouchPoints.has(pointerMeta.pointerId)) {
                    this.updatePointerTouchPoint(pointerMeta.pointerId, e.clientX, e.clientY);

                    if (this.startOrMovePointerPinch()) {
                        e.preventDefault();
                        return;
                    }
                }

                if (!isPrimaryPointer(e)) {
                    return;
                }

                const swipeResult = this.moveSwipe(e.clientX, e.clientY);
                if (swipeResult.handled && swipeResult.blockVertical) {
                    e.preventDefault();
                }
            });

            // Scroll button: Touch/pointer drag
            this.$button.on('pointerdown', (e) => {
                if (!isPrimaryPointer(e)) {
                    return;
                }

                if (this.isAdminEditBoxActive() || this.isAdminEditInteractionTarget(e.target)) {
                    return;
                }

                e.preventDefault();
                e.stopPropagation();

                const originalEvent = /** @type {any} */ (e.originalEvent || null);
                const pointerId = originalEvent && Number.isFinite(originalEvent.pointerId)
                    ? originalEvent.pointerId
                    : null;
                const buttonEl = /** @type {any} */ (e.currentTarget || null);
                if (buttonEl && pointerId !== null && typeof buttonEl.setPointerCapture === 'function') {
                    try {
                        buttonEl.setPointerCapture(pointerId);
                    } catch (_) {}
                }

                this.startSwipe(e.clientX, e.clientY, 'button');
            });

            this.$button.on('pointermove', (e) => {
                if (!isPrimaryPointer(e)) {
                    return;
                }

                const swipeResult = this.moveSwipe(e.clientX, e.clientY);
                if (swipeResult.handled && swipeResult.blockVertical) {
                    e.preventDefault();
                }
            });

            $(document).on('pointermove.connectStatsMapSwipeDocPtr', (e) => {
                if (!isPrimaryPointer(e) || !this.touchState || this.touchState.source !== 'button') {
                    return;
                }

                const swipeResult = this.moveSwipe(e.clientX, e.clientY);
                if (swipeResult.handled) {
                    e.preventDefault();
                }
            });

            $(document).on('pointerup', (e) => {
                const pointerMeta = getPointerMeta(e);
                const useTouchFallbackPinch = !!this.pointerPinchFallbackHandlers;
                if (!useTouchFallbackPinch && pointerMeta.pointerType === 'touch' && pointerMeta.pointerId !== null) {
                    this.removePointerTouchPoint(pointerMeta.pointerId);
                    if (this.pointerTouchPoints.size < 2) {
                        this.endPinch();
                    }
                }

                this.endSwipe();
            });

            $(document).on('pointercancel', (e) => {
                const pointerMeta = getPointerMeta(e);
                const useTouchFallbackPinch = !!this.pointerPinchFallbackHandlers;
                if (!useTouchFallbackPinch && pointerMeta.pointerType === 'touch' && pointerMeta.pointerId !== null) {
                    this.removePointerTouchPoint(pointerMeta.pointerId);
                    if (this.pointerTouchPoints.size < 2) {
                        this.endPinch();
                    }
                }

                this.endSwipe();
            });

            this.bindPointerModePinchFallback();
        } else {
            // ===== LEGACY TOUCH SUPPORT: For browsers without PointerEvent =====
            // Map viewport: Touch gestures (swipe + pinch zoom)
            // Does not activate on stars - stars will have their own drag handler with delay
            this.$mapViewport.on('touchstart', (e) => {
                const primaryTouch = e.originalEvent.touches && e.originalEvent.touches.length ? e.originalEvent.touches[0] : null;
                const primaryTarget = primaryTouch ? document.elementFromPoint(primaryTouch.clientX, primaryTouch.clientY) : e.target;
                if (this.isAdminEditBoxActive() || this.isAdminEditInteractionTarget(primaryTarget)) {
                    return;
                }

                // Two-finger pinch zoom
                if (e.originalEvent.touches.length === 2 && this.enablePinchMapZoom) {
                    this.endSwipe();
                    const touch1 = e.originalEvent.touches[0];
                    const touch2 = e.originalEvent.touches[1];
                    this.startPinch(touch1, touch2);
                    return;
                }

                if (e.originalEvent.touches.length !== 1) {
                    this.endSwipe();
                    return;
                }

                // Skip if touch is on a star (stars will have separate drag logic)
                const touch = e.originalEvent.touches[0];
                const target = document.elementFromPoint(touch.clientX, touch.clientY);
                if ($(target).closest('.stm-star').length > 0) {
                    return;
                }

                this.startSwipe(touch.clientX, touch.clientY, 'map');
            });

            this.$mapViewport.on('touchmove', (e) => {
                if (e.originalEvent.touches.length === 2 && this.enablePinchMapZoom) {
                    const touch1 = e.originalEvent.touches[0];
                    const touch2 = e.originalEvent.touches[1];
                    this.movePinch(touch1, touch2);
                    e.preventDefault();
                    return;
                }

                if (this.isSimultaneousSwipeScrollEnabled()) {
                    return;
                }

                if (e.originalEvent.touches.length !== 1) {
                    return;
                }

                const touch = e.originalEvent.touches[0];
                const swipeResult = this.moveSwipe(touch.clientX, touch.clientY);
                if (swipeResult.handled && swipeResult.blockVertical) {
                    e.preventDefault();
                }
            });

            // Scroll button: Touch drag
            this.$button.on('touchstart', (e) => {
                if (e.originalEvent.touches.length !== 1) {
                    this.endSwipe();
                    return;
                }

                const touch = e.originalEvent.touches[0];
                const target = document.elementFromPoint(touch.clientX, touch.clientY);
                if (this.isAdminEditBoxActive() || this.isAdminEditInteractionTarget(target)) {
                    return;
                }

                e.preventDefault();
                e.stopPropagation();

                this.startSwipe(touch.clientX, touch.clientY, 'button');
            });

            this.$button.on('touchmove', (e) => {
                if (this.isSimultaneousSwipeScrollEnabled()) {
                    return;
                }

                if (e.originalEvent.touches.length !== 1) {
                    return;
                }

                const touch = e.originalEvent.touches[0];
                const swipeResult = this.moveSwipe(touch.clientX, touch.clientY);
                if (swipeResult.handled && swipeResult.blockVertical) {
                    e.preventDefault();
                }
            });

            $(document).on('touchmove.connectStatsMapSwipeDoc', (e) => {
                if (!this.touchState) {
                    return;
                }

                const isButtonDrag = this.touchState.source === 'button';
                if (!isButtonDrag && !this.isSimultaneousSwipeScrollEnabled()) {
                    return;
                }

                const originalEvent = /** @type {any} */ (e.originalEvent || null);
                if (!originalEvent || !originalEvent.touches || originalEvent.touches.length !== 1) {
                    return;
                }

                const touch = originalEvent.touches[0];
                const swipeResult = this.moveSwipe(touch.clientX, touch.clientY);
                if (swipeResult.handled && (swipeResult.blockVertical || isButtonDrag)) {
                    e.preventDefault();
                }
            });

            $(document).on('touchend', () => {
                this.endSwipe();
                this.endPinch();
            });

            $(document).on('touchcancel', () => {
                this.endSwipe();
                this.endPinch();
            });
        }
    }

    /**
     * Clean up event listeners and resources
     */
    destroy() {
        // Clear any pending timeouts
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = null;
        }

        this.cancelInitialViewAnimation();
        this.restoreScrollRestoration();

        if (this.zoomScrollRaf !== null) {
            cancelAnimationFrame(this.zoomScrollRaf);
            this.zoomScrollRaf = null;
        }

        this.stopInertia();
        this.setNativeTouchScrollBlocked(false);
        this.isFollowingDefaultCenterSuspended = false;

        // Remove window resize listener
        $(window).off('resize', this.handleResize);
        $(window).off('scroll', this.handleWindowScroll);

        // Remove button events
        this.$button.off('mousedown');

        if ('PointerEvent' in window) {
            this.$mapViewport.off('pointerdown pointermove');
            this.$button.off('pointerdown pointermove');
            $(document).off('pointermove.connectStatsMapSwipeDocPtr');
            $(document).off('pointerup pointercancel');
            this.unbindPointerModePinchFallback();
        } else {
            this.$mapViewport.off('touchstart touchmove');
            this.$button.off('touchstart touchmove');
            $(document).off('touchmove.connectStatsMapSwipeDoc');
            $(document).off('touchend touchcancel');
        }

        // Clear touch state
        this.touchState = null;
        this.pinchState = null;
        this.pointerTouchPoints.clear();

        // Remove zoom controls
        if (this.enableZoom) {
            this.$container.find('.zoom-in, .zoom-out, .zoom-reset').off('click');
            $(document).off('keydown.connectStatsMapZoomHotkeys', this.handleZoomHotkeyKeydown);
        }

        // Remove pan mode handlers
        $(document).off('mousemove.connectStatsMapPan');
        $(document).off('mouseup.connectStatsMapPan');
        this.$stmapA.off('mousedown');
        this.$stmapA.css('cursor', '');

        // Remove ready class
        this.$container.removeClass('ready');
    }

    /**
     * Update debug crosshair position
     * @private
     */
    updateDebugCrosshair() {
        if (!this.debug || !this.$debugCrosshair.length) {
            return;
        }

        if (this.isFollowingDefaultCenter) {
            // Show crosshair at activePoint (red)
            const scaledActivePointPx = this.activePointPx * this.currentZoom;
            const scaledActivePointPy = this.activePointPy * this.currentZoom;

            // Calculate horizontal position
            const crosshairLeft = this.currentMapLeft + scaledActivePointPx;

            // Calculate vertical position relative to .connectStatsMap container
            const containerRect = this.$container[0].getBoundingClientRect();
            const mapViewportRect = this.$mapViewport[0].getBoundingClientRect();
            const mapOffsetTop = mapViewportRect.top - containerRect.top;
            const crosshairTop = mapOffsetTop + scaledActivePointPy - 10; // -10px to center the 20px crosshair

            this.$debugCrosshair.css({
                'left': crosshairLeft + 'px',
                'top': crosshairTop + 'px',
                'height': '20px',
                'background-color': 'red'
            }).show();
        } else {
            // Show crosshair at current center (orange)
            const centerX = this.viewportWidth / 2;
            this.$debugCrosshair.css({
                'left': centerX + 'px',
                'top': '0',
                'height': '100%',
                'background-color': 'orange'
            }).show();
        }
    }
}

const MapStarsUtils = {
    /**
    * Get config and country data for a star
    * Returns { cnf, isCountry, countryId } or null
     */
    getStarData($star) {
        if (!$star || !$star.length) {
            return null;
        }

        const isCity = $star.hasClass('iscity');
        const countryId = $star.data('country');
        const cnf = $star.data('cnf');
        const alias = $star.attr('data-alias') || '';
        if (!isCity) {
            return {
                cnf: cnf,
                isCountry: true,
                isCity: false,
                countryId: countryId,
                alias: alias,
                boxKey: String(countryId)
            };
        }

        const cityId = $star.data('city') || 0;
        const title = $star.attr('title') || '';

        return {
            cnf: cnf,
            isCountry: !isCity,
            isCity: isCity,
            countryId: countryId,
            cityId: cityId,
            alias: alias,
            title: title,
            boxKey: 'city:' + cityId
        };
/*

        const $country = $star.closest('.stm-country');
        if ($country.length) {
            const countryId = $country.data('country');
            const countryCnf = $country.data('cnf');
            const alias = $country.attr('data-alias') || '';

            return {
                cnf: countryCnf,
                isCountry: true,
                isCity: false,
                countryId: countryId,
                alias: alias,
                boxKey: String(countryId)
            };
        }

        if ($star.hasClass('mycity')) {
            const countryId = $star.data('country');
            const cityId = $star.data('city') || 0;
            const cnf = $star.data('cnf');
            const alias = $star.attr('data-alias') || '';
            const title = $star.attr('title') || '';

            const isCity = cityId > 0;

            return {
                cnf: cnf,
                isCountry: !isCity,
                isCity: isCity,
                countryId: countryId,
                cityId: cityId,
                alias: alias,
                title: title,
                boxKey: 'city:' + (alias || cityId || countryId)
            };
        }

        return null; */
    },

    getDefaultActiveStar($root) {
        if (!$root || !$root.length) {
            return $();
        }

        const $ismyMiracle = $root.find('.ismymiracle').first();
        if ($ismyMiracle.length) {
            return $ismyMiracle;
        }

        const $ismeStar = $root.find('.stm-star.isme').first();
        if ($ismeStar.length) {
            return $ismeStar;
        }

        const $ismaxStar = $root.find('.stm-star.ismax').first();
        if ($ismaxStar.length) {
            return $ismaxStar;
        }

        return $();
    },

    needCenterVertically($root) {
        return $root.find('.center-vertically').first().length > 0;
    }
};

class StatsMapBox {
    // Constants
    static INFO_BOX_WIDTH = 205;
    static CONTAINER_PADDING = 20;
    static STAR_SPACING = 17;
    static VERTICAL_SPACING = 9;

    /**
     * Info box controller for map stars
     * @param {*} container - Container selector or element
     * @param {string} statsUrl - Base URL for country statistics
     * @param {Object} positionOptions - Positioning configuration
     */
    constructor(container, statsUrl, positionOptions = {}) {
          this.$container = /** @type {any} */ ($(/** @type {any} */ (container)));

        // Validate required elements exist
        if (!this.$container.length) {
            console.error('StatsMapBox: Container not found');
            return;
        }

        this.$stmap = this.$container.find('.stmap');
        this.$stmapA = this.$stmap.find('.stmapA');
        this.$stmapB = this.$stmap.find('.stmapB');
        this.$infoBoxTemplate = this.$container.find('.stmap-info.stmap-info-tpl');

        if (!statsUrl || typeof statsUrl !== 'string') {
            console.error('StatsMapBox: statsUrl must be a string');
            return;
        }

        if (!this.$stmap.length || !this.$stmapA.length || !this.$stmapB.length || !this.$infoBoxTemplate.length) {
            console.error('StatsMapBox: Required child elements not found');
            return;
        }

        this.statsUrl = statsUrl;
        this.statsUrlPrefix = (positionOptions && positionOptions.statsUrlPrefix) || '';
        // Map<string boxKey, { $box, $star, currentSide, currentVerticalSide }>
        this.activeBoxes = new Map();
        this.lastOpenedKeys = [];
        this.starTouchStart = null;
        this.starTouchTapThreshold = 12;
        this.suppressNextStarClickUntil = 0;
        this.mapDragState = null;
        this.suppressCloseUntil = 0;
        this.dragThreshold = 8;
        this.dragSuppressMs = 350;

        // Noklusējuma opcijas
        this.positionOptions = {
            stickLeft: true,
            edgeOffset: 30,
            bottomOffset: 30,
            aboveOffset: 20,
            percentDecimals: 6,
            hideDelay: 50,
            ...positionOptions
        };

        this.init();
    }

    /**
     * Initialize event listeners
     * @private
     */
    init() {
        const getClientPoint = (event) => {
            const originalEvent = /** @type {any} */ (event && event.originalEvent ? event.originalEvent : event);
            if (originalEvent && originalEvent.touches && originalEvent.touches.length) {
                return {
                    x: originalEvent.touches[0].clientX,
                    y: originalEvent.touches[0].clientY
                };
            }

            if (originalEvent && originalEvent.changedTouches && originalEvent.changedTouches.length) {
                return {
                    x: originalEvent.changedTouches[0].clientX,
                    y: originalEvent.changedTouches[0].clientY
                };
            }

            if (event && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
                return {
                    x: event.clientX,
                    y: event.clientY
                };
            }

            return null;
        };

        this.$container.on('mousedown.statsMapBoxDrag pointerdown.statsMapBoxDrag touchstart.statsMapBoxDrag', '.stmap, .stscroll, .stmapA, .stmapB', (e) => {
            const point = getClientPoint(e);
            if (!point) {
                this.mapDragState = null;
                return;
            }

            this.mapDragState = {
                startX: point.x,
                startY: point.y,
                moved: false
            };
        });

        $(document).on('mousemove.statsMapBoxDrag pointermove.statsMapBoxDrag touchmove.statsMapBoxDrag', (e) => {
            if (!this.mapDragState) {
                return;
            }

            const point = getClientPoint(e);
            if (!point) {
                return;
            }

            const deltaX = point.x - this.mapDragState.startX;
            const deltaY = point.y - this.mapDragState.startY;
            const distance = Math.hypot(deltaX, deltaY);
            if (distance > this.dragThreshold) {
                this.mapDragState.moved = true;
            }
        });

        $(document).on('mouseup.statsMapBoxDrag pointerup.statsMapBoxDrag touchend.statsMapBoxDrag touchcancel.statsMapBoxDrag', () => {
            if (this.mapDragState && this.mapDragState.moved) {
                this.suppressCloseUntil = Date.now() + this.dragSuppressMs;
            }

            this.mapDragState = null;
        });

        this.$container.on('click', '.stm-star', (e) => this.handleStarClick(e));

        this.$container.on('touchstart', '.stm-star', (e) => {
            const originalEvent = /** @type {any} */ (e.originalEvent || null);
            if (!originalEvent || !originalEvent.touches || originalEvent.touches.length !== 1) {
                this.starTouchStart = null;
                return;
            }

            const touch = originalEvent.touches[0];
            const targetStar = $(touch.target).closest('.stm-star');
            if (!targetStar.length) {
                this.starTouchStart = null;
                return;
            }

            this.starTouchStart = {
                x: touch.clientX,
                y: touch.clientY,
                star: targetStar[0]
            };
        });

        this.$container.on('touchend', '.stm-star', (e) => {
            const touchStart = this.starTouchStart;
            this.starTouchStart = null;

            const originalEvent = /** @type {any} */ (e.originalEvent || null);
            if (!touchStart || !originalEvent || !originalEvent.changedTouches || !originalEvent.changedTouches.length) {
                return;
            }

            const touch = originalEvent.changedTouches[0];
            const deltaX = touch.clientX - touchStart.x;
            const deltaY = touch.clientY - touchStart.y;
            const distance = Math.hypot(deltaX, deltaY);
            if (distance > this.starTouchTapThreshold) {
                return;
            }

            const elementAtPoint = document.elementFromPoint(touch.clientX, touch.clientY);
            const $endStar = $(elementAtPoint).closest('.stm-star');
            if (!$endStar.length || $endStar[0] !== touchStart.star) {
                return;
            }

            this.suppressNextStarClickUntil = Date.now() + 500;
            this.handleStarClick(e, null, touch.clientX, touch.clientY);
            e.preventDefault();
        });

        this.$container.on('touchcancel', '.stm-star', () => {
            this.starTouchStart = null;
        });

        // Kliks uz info loga aizvēršanas pogas
        this.$container.on('click', '.stmap-info-close', (e) => {
            e.stopPropagation();
            const $box = $(e.target).closest('.stmap-info');
            const key = $box.data('stmap-box-key');
            if (key != null) {
                this._removeInfoBox(String(key));
            }
        });

        // Kliks uz info loga - atvērt statistiku
        this.$container.on('click', '.stmap-info:not(.stmap-info-tpl)', (e) => {
            if ($(e.target).closest('.stmap-info-close').length) {
                return;
            }
            const $box = $(e.currentTarget);
            const key = String($box.data('stmap-box-key'));
            if (key && this.activeBoxes.has(key)) {
                this._saveMapState();
                const alias = $box.data('stmap-box-alias');
                if (alias && this.statsUrlPrefix) {
                    const safePath = String(alias).split('/').map(s => encodeURIComponent(s)).join('/');
                    window.location.href = this.statsUrlPrefix + safePath + '/';
                } else if ($box.hasClass('iscity')) {
                    const cityId = $box.data('stmap-box-city-id');
                    window.location.href = this.statsUrl.replace(/[?&]country=$/, '?city=') + cityId;
                } else {
                    const countryId = $box.data('stmap-box-country-id');
                    window.location.href = this.statsUrl + countryId;
                }
            }
        });

        // Kliks ārpus zvaigznes
        $(document).on('click.statsMapBox', (e) => {
            if (Date.now() <= this.suppressCloseUntil) {
                return;
            }

            const $target = $(e.target);

            // Ja noklikšķināts uz zvaigznes, info loga, vai zoom controls - ignorēt
            if ($target.closest('.stm-star').length > 0 ||
                $target.closest('.stmap-info').length > 0 ||
                $target.closest('.zoom-controls').length > 0 ||
                $target.closest('.stzoom').length > 0 ||
                $target.closest('.zoom-in, .zoom-out, .zoom-reset').length > 0) {
                return;
            }

            this._removeAllBoxes();
        });

        // Listen for zoom changes to reposition active info boxes
        this.$container.on('mapZoomChanged', (event, payload) => {
            const source = payload && typeof payload.source === 'string' ? payload.source : '';
            if (source === 'reset') {
                this.activeBoxes.forEach(entry => {
                    entry.currentSide = null;
                    entry.currentVerticalSide = null;
                });
            }

            this.repositionActiveInfoBox();
        });

        // Restore info boxes from history.state (back/forward navigation)
        this._tryRestoreInfoBoxes();
    }

    /**
     * Remove a specific info box (with fade-out)
     * @private
     */
    _removeInfoBox(key) {
        const entry = this.activeBoxes.get(key);
        if (!entry) {
            return;
        }
        this.activeBoxes.delete(key);
        this.lastOpenedKeys = this.lastOpenedKeys.filter(k => k !== key);
        const $b = entry.$box;
        $b.removeClass('active');
        setTimeout(() => $b.remove(), 250);
    }

    /**
     * Remove all active info boxes
     * @private
     */
    _removeAllBoxes() {
        const entries = Array.from(this.activeBoxes.values());
        this.activeBoxes.clear();
        this.lastOpenedKeys = [];
        entries.forEach(entry => {
            const $b = entry.$box;
            $b.removeClass('active');
            setTimeout(() => $b.remove(), 250);
        });
    }

    /**
     * Save current map state into current history entry (called before navigation)
     * @private
     */
    _saveMapState() {
        try {
            const existing = history.state || {};
            // @ts-ignore
            window.glHistoryInstance.replaceState({
                ...existing,
                stmap: {
                    zoom: parseFloat(this.$container.css('--zoom-scale')) || 1,
                    mapLeft: parseFloat(this.$stmap.css('left')) || 0,
                    scrollY: window.scrollY || 0,
                    boxKeys: Array.from(this.activeBoxes.keys()),
                    lastOpenedKeys: this.lastOpenedKeys.slice()
                }
            }, '');
        } catch (e) {
            // history.replaceState may fail in some contexts
        }
    }

    /**
     * Find a star element by its boxKey
     * @private
     * @param {string} key - boxKey value
     * @returns {jQuery}
     */
    _findStarByBoxKey(key) {
        if (key.startsWith('city:')) {
            const cityId = key.slice('city:'.length);
            const $city = this.$stmapA.find('.stm-star.mycity[data-city="' + encodeURIComponent(cityId) + '"]');
            if ($city.length) {
                return $city;
            }
            // @ts-ignore
            return $();
        }

        const $country = this.$stmapA.find('.iscountry[data-country="' + encodeURIComponent(key) + '"]');
        if ($country.length) {
            return $country.is('.stm-star') ? $country : $country.find('.stm-star').first();
        }
        // @ts-ignore
        return $();
    }

    /**
     * Restore info boxes from history.state (called on page load after back/forward navigation)
     * @private
     */
    _tryRestoreInfoBoxes() {
        try {
            const state = history.state && history.state.stmap;
            if (!state) {
                return;
            }

            const boxKeys = state.boxKeys;
            if (!Array.isArray(boxKeys) || !boxKeys.length) {
                return;
            }

            for (const key of boxKeys) {
                const $star = this._findStarByBoxKey(key);
                if ($star.length) {
                    this._processStarClick($star);
                }
            }

            const restoredKeys = boxKeys.filter(k => this.activeBoxes.has(k));
            if (restoredKeys.length) {
                this._resolveVerticalCollisions();
                const orderedKeys = Array.isArray(state.lastOpenedKeys) && state.lastOpenedKeys.length
                    ? state.lastOpenedKeys.filter(k => this.activeBoxes.has(k))
                    : restoredKeys;
                this._applyGroupLayout(orderedKeys);
                this.lastOpenedKeys = orderedKeys;
            }
        } catch (e) {
            // Ignore restore errors
        }
    }

    /**
     * Handle click on star element — opens or closes info box(es)
     * Uses elementsFromPoint to detect all overlapping stars at the click position
     * @private
     * @param {Event} e - Click event
     */
    handleStarClick(e, forcedStar = null, touchClientX = null, touchClientY = null) {
        if (e && e.type === 'click' && Date.now() <= this.suppressNextStarClickUntil) {
            return;
        }

        /* const $toggleAbc = this.$container.find('.toggleabc');
        if ($toggleAbc.length && $toggleAbc.hasClass('on')) {
            return;
        } */

        let starsToProcess = [];

        if (forcedStar && forcedStar.length) {
            starsToProcess = [forcedStar];
        } else {
            // Meklēt visas zvaigznes klika punktā (pārklāšanās gadījums)
            // @ts-ignore
            const originalEvent = /** @type {any} */ (e.originalEvent || e);
            let clientX = Number.isFinite(originalEvent.clientX) ? originalEvent.clientX : null;
            let clientY = Number.isFinite(originalEvent.clientY) ? originalEvent.clientY : null;
            if (clientX === null && touchClientX !== null) { clientX = touchClientX; }
            if (clientY === null && touchClientY !== null) { clientY = touchClientY; }

            if (clientX !== null && clientY !== null && document.elementsFromPoint) {
                const els = document.elementsFromPoint(clientX, clientY);
                const seen = new Set();
                for (const el of els) {
                    const $s = $(el).closest('.stm-star');
                    if ($s.length && !$s.hasClass('star-in-country') && !$s.hasClass('star-in-button') && !seen.has($s[0])) {
                        seen.add($s[0]);
                        starsToProcess.push($s);
                    }
                }
            }

            if (!starsToProcess.length) {
                const $s = $(e.target).closest('.stm-star');
                if ($s.length) {
                    starsToProcess.push($s);
                }
            }
        }

        // Kārtot: ja ir .mycity zvaigzne, tās valsts zvaigzne iet pirmā (ja ir), tad pilsēta, tad pārējās
        if (starsToProcess.length > 1) {
            const cityIdx = starsToProcess.findIndex($s => $s.hasClass('mycity'));
            if (cityIdx !== -1) {
                const $cityStar = starsToProcess[cityIdx];
                const cityData = MapStarsUtils.getStarData($cityStar);
                const cityCountryId = cityData ? String(cityData.countryId) : null;

                const countryIdx = cityCountryId
                    ? starsToProcess.findIndex(($s, i) => {
                        if (i === cityIdx) return false;
                        const d = MapStarsUtils.getStarData($s);
                        return d && d.isCountry && String(d.countryId) === cityCountryId;
                    })
                    : -1;

                const sorted = [];
                if (countryIdx !== -1) {
                    sorted.push(starsToProcess[countryIdx]);
                }
                sorted.push($cityStar);
                for (let i = 0; i < starsToProcess.length; i++) {
                    if (i !== cityIdx && i !== countryIdx) {
                        sorted.push(starsToProcess[i]);
                    }
                }
                starsToProcess = sorted;
            }
        }

        // Noskaidrot boxKey kopai, kas tiks atvērta ar šo klikšķi
        const newKeys = new Set();
        for (const $star of starsToProcess) {
            const starData = MapStarsUtils.getStarData($star);
            if (starData) {
                newKeys.add(starData.boxKey);
            }
        }

        // Aizvērt visus info logus, kas nav jaunajos
        const keysToRemove = [];
        this.activeBoxes.forEach((entry, key) => {
            if (!newKeys.has(key)) {
                keysToRemove.push(key);
            }
        });
        keysToRemove.forEach(key => this._removeInfoBox(key));

        const openedKeys = [];
        for (const $star of starsToProcess) {
            const starData = MapStarsUtils.getStarData($star);
            const key = starData ? starData.boxKey : null;
            const wasOpen = key !== null && this.activeBoxes.has(key);
            this._processStarClick($star);
            if (key !== null && !wasOpen && this.activeBoxes.has(key)) {
                openedKeys.push(key);
            }
        }

        this._resolveVerticalCollisions();
        this._applyGroupLayout(openedKeys);
        this.lastOpenedKeys = openedKeys.length > 0 ? openedKeys : this.lastOpenedKeys;
    }

    /**
     * Open or toggle info box for a single star
     * @private
     */
    _processStarClick($star) {
        if (!$star || !$star.length || $star.hasClass('star-in-country') || $star.hasClass('star-in-button')) {
            return;
        }

        const starData = MapStarsUtils.getStarData($star);
        if (!starData) {
            return;
        }

        const key = starData.boxKey;

        if (this.activeBoxes.has(key)) {
            // Jau atvērts — aizvērt (toggle)
            this._removeInfoBox(key);
            return;
        }

        const $box = this._createAndFillBox($star, starData);
        this.activeBoxes.set(key, { $box, $star, currentSide: null, currentVerticalSide: null });
        this._positionInfoBox(key);

        // Activate in next frame so CSS transition fires
        requestAnimationFrame(() => {
            $box.addClass('active');
        });
    }

    /**
     * Clone the template and populate it with country data
     * @private
     */
    _createAndFillBox($star, starData) {
        const countryId = starData.countryId;
        const key = starData.boxKey;
        const cnf = starData.cnf;
        const isMe = $star.hasClass('isme');
        const isCity = !!starData.isCity;
        const alias = starData.alias || '';

        let parsedCnf = cnf;
        if (typeof cnf === 'string') {
            try {
                parsedCnf = JSON.parse(cnf);
            } catch (parseError) {
                parsedCnf = [0,0];
            }
        }

        const connectionsCount = (parsedCnf && parsedCnf[0]) || 0;
        const miraclesCount = (parsedCnf && parsedCnf[1]) || 0;

        //const countryData = this.countryNames[countryId] || [null, 'Unknown', 'Unknown', ''];
        const countryData = ConnectStatsMapState.getCountryData(countryId) || [null, 'Unknown', 'Unknown', ''];
        const countryCode = countryData[0];
        const countryName = (countryData[1] || countryData[2] || 'Unknown');
        const countryAddName = (countryData[3] || '').trim();
        const cityName = isCity
            ? (starData.title || 'Unknown')
            : '';

        const $box = this.$infoBoxTemplate.clone()
            .removeClass('stmap-info-tpl')
            .data('stmap-box-key', key)
            .data('stmap-box-alias', alias)
            .data('stmap-box-country-id', countryId)
            .data('stmap-box-city-id', isCity ? (starData.cityId || 0) : 0);

        if (isCity) {
            $box.addClass('iscity');
        }

        if (countryCode) {
            $box.find('.flag img')
                .attr('src', '/img/fl/' + countryCode.toLowerCase() + '.png')
                .attr('srcset', '/img/fl/' + countryCode.toLowerCase() + '.svg');
        }

        const $starInCountry = $box.find('.star-in-country');
        if (isMe) {
            $starInCountry.show();
        } else {
            $starInCountry.hide();
        }

        $box.find('.countryName').text(countryName);
        if (countryAddName != '') {
            $box.find('.countryName').append($('<span>').addClass('addName').text(' (' + countryAddName + ')'));
        }
        $box.find('.cityName').text(cityName);
        $box.find('.c').text(connectionsCount);
        $box.find('.m').text(miraclesCount);

        this.$stmapB.append($box);
        return $box;
    }

    /**
     * Resolve vertical collisions between open info boxes
     * Sorts boxes by vertical position and pushes overlapping ones downward
     * @private
     */
    _resolveVerticalCollisions() {
        if (this.activeBoxes.size <= 1) {
            return;
        }

        const stmapHeight = this.$stmap.height() || 1;
        const gap = 5;

        const boxes = [];
        this.activeBoxes.forEach(entry => {
            const topVal = entry.$box.css('top');
            const topPx = typeof topVal === 'string' && topVal.endsWith('%')
                ? (parseFloat(topVal) / 100) * stmapHeight
                : (parseFloat(topVal) || 0);
            const height = entry.$box.outerHeight() || 120;
            boxes.push({ entry, topPx, height });
        });

        boxes.sort((a, b) => a.topPx - b.topPx);

        for (let i = 1; i < boxes.length; i++) {
            const prev = boxes[i - 1];
            const curr = boxes[i];
            const minTop = prev.topPx + prev.height + gap;
            if (curr.topPx < minTop) {
                curr.topPx = minTop;
                curr.entry.$box.css('top', this.pxToPercent(curr.topPx, stmapHeight));
            }
        }
    }

    /**
     * Calculate positioning context data
     * @private
     */
    calculatePositionContext($star, $box) {
        // beam animācijām nav tieši left & top, tas ir parent elementam .sceneBeam;
        // bet ir margin left & top uz pašas zvaigznes elementa
        const $starForPos = $star.hasClass('beamstar') ? $star.closest('.sceneBeam') : $star;
        // Get star position from computed style in current (already zoomed) map size
        const starOffsetLeft = parseFloat($starForPos.css('left')) || 0;
        const starOffsetTop = parseFloat($starForPos.css('top')) || 0;
        const starMarginLeft = parseFloat($star.css('margin-left')) || 0;
        const starMarginTop = parseFloat($star.css('margin-top')) || 0;
        const starWidth = $star.outerWidth();
        const starHeight = $star.outerHeight();

        const stmapLeft = parseInt(this.$stmap.css('left')) || 0;
        const infoBoxHeight = $box.outerHeight() || 120;
        const windowWidth = $(window).width();
        const windowHeight = $(window).height();

        const stmapRect = this.$stmap[0].getBoundingClientRect();
        const stmapViewportTop = stmapRect.top;
        const stmapGlobalLeft = stmapRect.left;

        return {
            starOffsetLeft,
            starOffsetTop,
            starMarginLeft,
            starMarginTop,
            starWidth,
            starHeight,
            stmapLeft,
            infoBoxHeight,
            windowWidth,
            windowHeight,
            stmapViewportTop,
            stmapGlobalLeft,
            edgeOffset: this.positionOptions.edgeOffset,
            bottomOffset: this.positionOptions.bottomOffset,
            aboveOffset: this.positionOptions.aboveOffset,
            stickLeft: this.positionOptions.stickLeft
        };
    }

    /**
     * Calculate horizontal position for info box
     * @private
     */
    calculateHorizontalPosition(context, entry) {
        const {
            starOffsetLeft,
            starMarginLeft,
            starWidth,
            windowWidth,
            edgeOffset,
            stickLeft
        } = context;

        const stmapRect = this.$stmap[0].getBoundingClientRect();

        const starScaledLeft = starOffsetLeft + starMarginLeft;
        const starScaledWidth = starWidth;
        const starCenterX = starScaledLeft + (starScaledWidth / 2);

        // If we have a saved side from previous positioning, use it (prevents flipping during zoom)
        if (entry.currentSide === 'right') {
            const candidateLeft = starCenterX + StatsMapBox.STAR_SPACING;
            return candidateLeft;
        } else if (entry.currentSide === 'left') {
            const candidateLeft = starCenterX - StatsMapBox.INFO_BOX_WIDTH - StatsMapBox.STAR_SPACING;
            return candidateLeft;
        }

        // Try positioning to the right of star
        let candidateLeft = starCenterX + StatsMapBox.STAR_SPACING;
        let screenLeft = stmapRect.left + candidateLeft;
        let screenRight = screenLeft + StatsMapBox.INFO_BOX_WIDTH;

        const rightFits = screenLeft >= StatsMapBox.CONTAINER_PADDING &&
                          screenRight <= (windowWidth - StatsMapBox.CONTAINER_PADDING);

        if (rightFits) {
            entry.currentSide = 'right';
            return candidateLeft;
        }

        // Try positioning to the left of star
        if (!stickLeft) {
            candidateLeft = starCenterX - StatsMapBox.INFO_BOX_WIDTH - StatsMapBox.STAR_SPACING;
            screenLeft = stmapRect.left + candidateLeft;
            screenRight = screenLeft + StatsMapBox.INFO_BOX_WIDTH;

            const leftFits = screenLeft >= StatsMapBox.CONTAINER_PADDING &&
                             screenRight <= (windowWidth - StatsMapBox.CONTAINER_PADDING);

            if (leftFits) {
                entry.currentSide = 'left';
                return candidateLeft;
            }

            // Fallback positioning
            if (screenRight > windowWidth - StatsMapBox.CONTAINER_PADDING - edgeOffset) {
                // Stick to right edge
                entry.currentSide = 'edge-right';
                return windowWidth - StatsMapBox.INFO_BOX_WIDTH - edgeOffset - stmapRect.left;
            } else if (screenLeft < StatsMapBox.CONTAINER_PADDING + edgeOffset) {
                // Stick to left edge
                entry.currentSide = 'edge-left';
                return edgeOffset - stmapRect.left;
            } else {
                entry.currentSide = 'right';
                return starCenterX + StatsMapBox.STAR_SPACING;
            }
        }

        // stickLeft === true
        screenLeft = stmapRect.left + candidateLeft;
        screenRight = screenLeft + StatsMapBox.INFO_BOX_WIDTH;

        if (screenRight > windowWidth - StatsMapBox.CONTAINER_PADDING - edgeOffset ||
            screenLeft < StatsMapBox.CONTAINER_PADDING + edgeOffset) {
            // Stick to right edge
            entry.currentSide = 'edge-right';
            return windowWidth - StatsMapBox.INFO_BOX_WIDTH - edgeOffset - stmapRect.left;
        }

        entry.currentSide = 'right';
        return candidateLeft;
    }

    /**
     * Calculate vertical position for info box
     * @private
     */
    calculateVerticalPosition(context, horizontalLeft, entry) {
        const {
            starOffsetTop,
            starMarginTop,
            starHeight,
            infoBoxHeight,
            windowHeight,
            bottomOffset,
            aboveOffset
        } = context;

        const stmapRect = this.$stmap[0].getBoundingClientRect();

        const starScaledTop = starOffsetTop + starMarginTop;
        const starScaledHeight = starHeight;
        const starCenterY = starScaledTop + (starScaledHeight / 2);

        // If we have a saved vertical side, use it (prevents flipping during zoom)
        if (entry.currentVerticalSide === 'below') {
            return starCenterY + StatsMapBox.VERTICAL_SPACING;
        } else if (entry.currentVerticalSide === 'above') {
            return starCenterY - infoBoxHeight - StatsMapBox.VERTICAL_SPACING;
        } else if (entry.currentVerticalSide === 'bottom-edge') {
            return windowHeight - infoBoxHeight - bottomOffset - stmapRect.top;
        }

        let candidateTop = starCenterY + StatsMapBox.VERTICAL_SPACING;
        let screenTop = stmapRect.top + candidateTop;
        let screenBottom = screenTop + infoBoxHeight;

        // Check if it fits vertically below
        if (screenBottom <= windowHeight - bottomOffset) {
            entry.currentVerticalSide = 'below';
            return candidateTop;
        }

        // Try positioning above the star
        candidateTop = starCenterY - infoBoxHeight - StatsMapBox.VERTICAL_SPACING;
        screenTop = stmapRect.top + candidateTop;

        // If it fits above, use that
        if (screenTop >= StatsMapBox.CONTAINER_PADDING) {
            entry.currentVerticalSide = 'above';
            return candidateTop;
        }

        // Otherwise, stick to bottom of viewport
        entry.currentVerticalSide = 'bottom-edge';
        return windowHeight - infoBoxHeight - bottomOffset - stmapRect.top;
    }

    /**
     * Apply position to info box
     * @private
     */
    applyPosition($box, left, top) {
        // Ensure position is not negative
        left = Math.max(0, left);
        top = Math.max(0, top);

        const stmapWidth = this.$stmap.width() || 1;
        const stmapHeight = this.$stmap.height() || 1;
        const leftPercent = this.pxToPercent(left, stmapWidth);
        const topPercent = this.pxToPercent(top, stmapHeight);

        $box.css({
            left: leftPercent,
            top: topPercent
        });
    }

    pxToPercent(pxValue, baseSize) {
        const base = parseFloat(baseSize);
        const value = parseFloat(pxValue);
        const decimals = Number.isFinite(this.positionOptions.percentDecimals)
            ? this.positionOptions.percentDecimals
            : 6;

        if (!Number.isFinite(base) || base <= 0 || !Number.isFinite(value)) {
            return '0%';
        }

        return ((value / base) * 100).toFixed(Math.max(0, Math.min(10, decimals))) + '%';
    }

    /**
     * Position an info box by its country key
     * @private
     */
    _positionInfoBox(key) {
        const entry = this.activeBoxes.get(key);
        if (!entry) {
            return;
        }
        const context = this.calculatePositionContext(entry.$star, entry.$box);
        const left = this.calculateHorizontalPosition(context, entry);
        const top = this.calculateVerticalPosition(context, left, entry);
        this.applyPosition(entry.$box, left, top);
    }

    /**
     * Position info box relative to star (public alias kept for compatibility)
     */
    positionInfoBox(key) {
        this._positionInfoBox(key);
    }

    /**
     * Apply group layout: shared left position and forced vertical order
     * @private
     */
    _applyGroupLayout(orderedKeys) {
        if (!orderedKeys || orderedKeys.length <= 1) {
            return;
        }

        const entries = orderedKeys
            .map(k => this.activeBoxes.get(k))
            .filter(e => !!e);

        if (entries.length <= 1) {
            return;
        }

        // Align all boxes to the first box's left position and side
        const firstEntry = entries[0];
        const sharedLeft = firstEntry.$box.css('left');
        for (let i = 1; i < entries.length; i++) {
            entries[i].$box.css('left', sharedLeft);
            entries[i].currentSide = firstEntry.currentSide;
        }

        // Stack vertically in order, starting from the topmost position
        const stmapHeight = this.$stmap.height() || 1;
        const gap = 5;
        const tops = entries.map(e => {
            const topVal = e.$box.css('top');
            return typeof topVal === 'string' && topVal.endsWith('%')
                ? (parseFloat(topVal) / 100) * stmapHeight
                : (parseFloat(topVal) || 0);
        });
        let currentTop = Math.min(...tops);

        for (const entry of entries) {
            entry.$box.css('top', this.pxToPercent(currentTop, stmapHeight));
            const height = entry.$box.outerHeight() || 120;
            currentTop += height + gap;
        }
    }

    /**
     * Reposition all active info boxes (called when zoom changes)
     */
    repositionActiveInfoBox() {
        // Reposition the first box by its star, others will follow via group layout
        const orderedKeys = this.lastOpenedKeys.filter(k => this.activeBoxes.has(k));

        if (orderedKeys.length > 0) {
            // Only recalculate position for the first (anchor) box
            this._positionInfoBox(orderedKeys[0]);
        }

        // Reposition any boxes not in the saved order (standalone)
        this.activeBoxes.forEach((entry, key) => {
            if (!orderedKeys.includes(key)) {
                this._positionInfoBox(key);
            }
        });

        this._applyGroupLayout(orderedKeys);
    }

    /**
     * Clean up event listeners and resources
     */
    destroy() {
        // Remove all active info boxes from DOM
        this.activeBoxes.forEach(entry => {
            entry.$box.remove();
        });
        this.activeBoxes.clear();

        // Remove event listeners
        this.$container.off('click', '.stm-star');
        this.$container.off('click', '.stmap-info-close');
        this.$container.off('click', '.stmap-info:not(.stmap-info-tpl)');
        this.$container.off('mapZoomChanged');
        this.$container.off('.statsMapBoxDrag');
        $(document).off('click.statsMapBox');
        $(document).off('.statsMapBoxDrag');

        // Clear references
        this.activeBoxes = new Map();
    }
}



// @ts-ignore
window.ConnectStatsMap = ConnectStatsMap;
// @ts-ignore
window.MapStarsUtils = MapStarsUtils;
// @ts-ignore
window.StatsMapBox = StatsMapBox;
// @ts-ignore
window.ConnectStatsMapState = ConnectStatsMapState;